import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  Save,
  Users, 
  Gift,
  Trash2,
  Plus,
  Loader2,
  Upload,
  Image,
  Palette,
  Eye,
  ExternalLink,
  Play,
  Monitor,
  QrCode,
  Link2,
  Copy,
  Download,
  Share2,
  Store,
  Trophy,
  CheckCircle2,
  Mail,
  Send,
  Bell,
  Clock,
  Package,
  Video,
  DollarSign,
  AlertTriangle,
  RotateCcw,
  Tag,
  ChevronDown,
  X,
  FileSpreadsheet,
  ArrowRight,
  Check,
  FileText,
  Pen,
  Heart,
  Sparkles,
  Crown,
  Building2,
  Camera
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { drawsAPI } from "../lib/api";
import api from "../lib/api";
import { getImageUrl } from "../lib/utils";
import { QRCodeSVG } from "qrcode.react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon, UserPlus, Search, FolderOpen } from "lucide-react";
import MediaSelector from "../components/MediaSelector";
import PhotoWallManager from "../components/PhotoWallManager";
import DrawGuide from "../components/DrawGuide";

// Preview component for the public page - 3 modes: show, results, register
const DrawPreview = ({ draw, previewMode = "show" }) => {
  const bgStyle = draw?.background_type === "image" && draw?.background_image
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${draw.background_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : draw?.background_type === "gradient" && draw?.background_gradient
      ? { background: draw.background_gradient }
      : { background: `linear-gradient(135deg, ${draw?.background_color || '#1e3a5f'} 0%, #1e1b4b 100%)` };

  // Demo results data
  const demoResults = [
    { name: "Marie Dupont", prize: draw?.prizes?.[0]?.name || "Lot 1" },
    { name: "Jean Martin", prize: draw?.prizes?.[1]?.name || draw?.prizes?.[0]?.name || "Lot 1" },
    { name: "Sophie Bernard", prize: draw?.prizes?.[2]?.name || draw?.prizes?.[0]?.name || "Lot 1" }
  ];

  // ============ SHOW MODE - 3 Column Layout (Projector style) ============
  if (previewMode === "show") {
    return (
      <div className="h-full overflow-hidden" style={bgStyle}>
        <div className="h-full flex p-3 gap-2">
          {/* Left Column - Countdown & Partners */}
          <div className="w-1/4 flex flex-col gap-2">
            {/* Countdown */}
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
              <p className="text-yellow-400 text-[10px] font-semibold uppercase tracking-wider mb-1">TIRAGE DANS</p>
              <p className="text-white text-lg font-black">3j 12h</p>
              <p className="text-white/50 text-[8px]">Les lots vous attendent !</p>
            </div>
            
            {/* Participants */}
            <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center">
              <Users className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <p className="text-white text-base font-bold">{draw?.participants?.length || 0}</p>
              <p className="text-white/50 text-[8px]">Participants</p>
            </div>
            
            {/* Partners */}
            {draw?.partner_carousel_enabled && draw?.partner_logos?.length > 0 && (
              <div className="bg-white/10 backdrop-blur rounded-lg p-2 flex-1 flex flex-col">
                <p className="text-white text-[9px] font-semibold text-center mb-2">
                  {draw.partner_carousel_title || "Nos partenaires"}
                </p>
                <div className="flex-1 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-2">
                    {draw.partner_logos[0]?.url && (
                      <img src={getImageUrl(draw.partner_logos[0].url)} alt="" className="h-8 w-auto object-contain" />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Center Column - Title & Prizes */}
          <div className="w-1/2 flex flex-col">
            <div className="text-center mb-2">
              <h1 className="text-white text-xl font-black" style={{ fontFamily: 'Outfit' }}>
                {draw?.title || "Titre du tirage"}
              </h1>
              <Badge className="bg-yellow-500/20 text-yellow-400 text-[8px] mt-1">
                <Gift className="w-2 h-2 mr-1" />
                LOTS À GAGNER
              </Badge>
            </div>

            {/* Prize Card */}
            {draw?.prizes?.length > 0 && (
              <div className="flex-1 flex items-center justify-center">
                <div className="bg-gradient-to-br from-yellow-500/20 to-amber-500/10 backdrop-blur rounded-xl p-3 border border-yellow-500/30 w-full max-w-[200px]">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center text-white text-xs font-bold">1</div>
                    <h3 className="text-white text-sm font-bold flex-1 truncate">{draw.prizes[0]?.name || "Lot"}</h3>
                    <Badge className="bg-yellow-500 text-black text-[8px]">x{draw.prizes[0]?.quantity || 1}</Badge>
                  </div>
                  <div className="h-16 bg-gradient-to-br from-yellow-500/20 to-amber-600/20 rounded-lg flex items-center justify-center">
                    {draw.prizes[0]?.image_url ? (
                      <img src={getImageUrl(draw.prizes[0].image_url)} alt="" className="h-full w-full object-cover rounded-lg" />
                    ) : (
                      <Gift className="w-8 h-8 text-yellow-400/50" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-auto">
              <p className="text-white/30 text-[8px]">Propulsé par</p>
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Outfit' }}>
                <span className="text-blue-400">TIR</span>EO
              </p>
            </div>
          </div>

          {/* Right Column - Organizer & Beneficiary */}
          <div className="w-1/4 flex flex-col gap-2">
            {/* Organizer */}
            <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center flex-1">
              <p className="text-white/60 text-[8px] uppercase tracking-wider mb-1">Organisé par</p>
              {draw?.organizer_logo ? (
                <div className="bg-white rounded-lg p-1 mb-1 mx-auto w-fit">
                  <img src={getImageUrl(draw.organizer_logo)} alt="" className="h-8 w-auto object-contain" />
                </div>
              ) : (
                <div className="w-8 h-8 mx-auto bg-white/20 rounded-lg flex items-center justify-center mb-1">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
              )}
              <p className="text-white text-[10px] font-bold">{draw?.organizer_name || "Organisateur"}</p>
            </div>

            {/* Beneficiary */}
            {draw?.beneficiary_name && (
              <div className="bg-gradient-to-br from-pink-500/20 to-rose-500/10 backdrop-blur rounded-lg p-2 text-center flex-1 border border-pink-500/30">
                <p className="text-pink-300 text-[8px] uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                  <Heart className="w-2 h-2" />
                  Au profit de
                </p>
                {draw?.beneficiary_logo ? (
                  <div className="bg-white rounded-lg p-1 mb-1 mx-auto w-fit">
                    <img src={getImageUrl(draw.beneficiary_logo)} alt="" className="h-8 w-auto object-contain" />
                  </div>
                ) : (
                  <div className="w-8 h-8 mx-auto bg-pink-500/30 rounded-lg flex items-center justify-center mb-1">
                    <Heart className="w-4 h-4 text-pink-400" />
                  </div>
                )}
                <p className="text-pink-300 text-[10px] font-bold">{draw.beneficiary_name}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============ RESULTS MODE - With demo data ============
  if (previewMode === "results") {
    return (
      <div className="h-full overflow-hidden" style={bgStyle}>
        <div className="h-full flex p-3 gap-2">
          {/* Left Column */}
          <div className="w-1/4 flex flex-col gap-2">
            <div className="bg-green-500/20 backdrop-blur rounded-lg p-3 text-center border border-green-500/30">
              <Trophy className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-green-400 text-[10px] font-bold uppercase">Terminé</p>
            </div>
            <div className="bg-yellow-500/20 backdrop-blur rounded-lg p-2 text-center">
              <Crown className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
              <p className="text-white text-lg font-bold">{demoResults.length}</p>
              <p className="text-white/50 text-[8px]">Gagnants</p>
            </div>
          </div>

          {/* Center Column - Winners */}
          <div className="w-1/2 flex flex-col">
            <div className="text-center mb-2">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Sparkles className="w-3 h-3 text-yellow-400" />
                <span className="text-yellow-400 text-[10px] font-bold uppercase">Résultats</span>
                <Sparkles className="w-3 h-3 text-yellow-400" />
              </div>
              <h1 className="text-white text-lg font-black" style={{ fontFamily: 'Outfit' }}>
                {draw?.title || "Titre du tirage"}
              </h1>
            </div>

            {/* Winners List (Demo) */}
            <div className="flex-1 space-y-1 overflow-hidden">
              {demoResults.slice(0, 3).map((result, idx) => (
                <div key={idx} className="bg-white/10 rounded-lg p-2 flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-[10px] font-bold text-white">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[10px] font-bold truncate">{result.name}</p>
                    <p className="text-white/50 text-[8px] truncate">{result.prize}</p>
                  </div>
                  <Sparkles className="w-3 h-3 text-yellow-400" />
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="text-center mt-2">
              <p className="text-white/30 text-[8px]">Propulsé par</p>
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Outfit' }}>
                <span className="text-blue-400">TIR</span>EO
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="w-1/4 flex flex-col gap-2">
            <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center flex-1">
              <p className="text-white/60 text-[8px] uppercase tracking-wider mb-1">Organisé par</p>
              <p className="text-white text-[10px] font-bold">{draw?.organizer_name || "Organisateur"}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center">
              <p className="text-white/60 text-[8px]">Félicitations aux gagnants !</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ REGISTER MODE - Participation form ============
  return (
    <div className="h-full overflow-auto rounded-xl" style={bgStyle}>
      <div className="min-h-full p-4">
        {/* Header */}
        <div className="text-center mb-4">
          {draw?.organizer_logo ? (
            <img 
              src={getImageUrl(draw.organizer_logo)} 
              alt={draw?.organizer_name}
              className="h-12 mx-auto mb-3 object-contain rounded-xl"
            />
          ) : (
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-white/20 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
          )}
          
          <h2 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>
            {draw?.title || "Titre du tirage"}
          </h2>
          <p className="text-white/70 text-xs">{draw?.organizer_name || "Organisateur"}</p>
        </div>

        {/* Prizes */}
        {draw?.prizes?.length > 0 && (
          <div className="mb-4">
            <h3 className="text-[10px] font-medium text-white/80 mb-2 text-center">Lots à gagner</h3>
            <div className="space-y-1">
              {draw.prizes.slice(0, 2).map((prize, index) => (
                <div 
                  key={prize.id || index}
                  className="flex items-center gap-2 bg-white/10 rounded-lg p-2"
                >
                  <div 
                    className="w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ backgroundColor: draw?.accent_color || '#eab308' }}
                  >
                    {index + 1}
                  </div>
                  <p className="text-white text-xs font-medium flex-1 truncate">{prize.name}</p>
                  <Badge className="bg-white/20 text-white text-[8px]">x{prize.quantity}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registration Form Preview */}
        <div className="max-w-[200px] mx-auto">
          <div className="bg-white/10 rounded-xl p-3 space-y-2">
            <Input 
              placeholder="Votre nom / pseudo" 
              className="h-7 text-[10px] bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled
            />
            <Input 
              placeholder="Email" 
              className="h-7 text-[10px] bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled
            />
            {draw?.custom_field_label && (
              <Input 
                placeholder={draw.custom_field_label}
                className="h-7 text-[10px] bg-white/10 border-white/20 text-white placeholder:text-white/40"
                disabled
              />
            )}
            <Button 
              className="w-full h-7 rounded-full text-[10px] font-medium text-white"
              style={{ backgroundColor: draw?.accent_color || '#eab308' }}
              disabled
            >
              Participer
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 text-white/40 text-[8px]">
          <p>Propulsé par TIREO</p>
        </div>
      </div>
    </div>
  );
};

export default function DrawEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === "new";
  
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [previewMode, setPreviewMode] = useState("show");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  
  // Certificate export modal
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificateData, setCertificateData] = useState({
    supervisor_type: "huissier", // "huissier" or "controleur"
    supervisor_name: "",
    supervisor_role: "",
    supervisor_date: new Date().toISOString().split('T')[0],
    supervisor_time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    notes: "",
    signature: "" // Base64 signature image
  });
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [newParticipant, setNewParticipant] = useState({ name: "", email: "", phone: "", custom_field_value: "", pseudo: "" });
  const [editingParticipant, setEditingParticipant] = useState(null); // For editing existing participant
  const [participantSearch, setParticipantSearch] = useState(""); // Search filter for participants
  const [newPrize, setNewPrize] = useState({ 
    name: "", 
    description: "", 
    quantity: 1, 
    image_url: "",
    video_url: "",
    media_type: "image",
    partner_name: "",
    partner_logo_url: "",
    value: ""
  });
  const [editingPrize, setEditingPrize] = useState(null); // For editing existing prize
  const [savingPrize, setSavingPrize] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [addingPrize, setAddingPrize] = useState(false);
  const [updatingWithdrawal, setUpdatingWithdrawal] = useState(null);
  const [uploadingPrizeImage, setUploadingPrizeImage] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [cancellingPrize, setCancellingPrize] = useState(null);
  
  // Email sending states
  const [sendingWinnerEmails, setSendingWinnerEmails] = useState(false);
  const [sendingReminderEmails, setSendingReminderEmails] = useState(false);
  const [sendingResultsEmails, setSendingResultsEmails] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [loadingEmailStatus, setLoadingEmailStatus] = useState(false);
  
  const prizeImageRef = useRef(null);
  const editPrizeImageRef = useRef(null);
  const editPrizePartnerLogoRef = useRef(null);
  
  // Countdown state
  const [countdown, setCountdown] = useState({ participation: null, draw: null });
  
  const logoInputRef = useRef(null);
  const beneficiaryLogoRef = useRef(null);
  const bgInputRef = useRef(null);
  const csvInputRef = useRef(null);
  
  // Beneficiary logo upload state
  const [uploadingBeneficiaryLogo, setUploadingBeneficiaryLogo] = useState(false);
  
  // Media gallery selector
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  
  // CSV Import states
  const [showCsvMapping, setShowCsvMapping] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [columnMapping, setColumnMapping] = useState({
    prenom: "",
    nom: "",
    email: "",
    phone: "",
    custom_field: "",
    ticket_number: ""
  });
  const [importingCsv, setImportingCsv] = useState(false);
  
  // Date states for editing
  const [dates, setDates] = useState({
    start_date: null,
    draw_date: null,
    participation_deadline: null,
    publication_end_date: null
  });

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      title: "",
      description: "",
      organizer_logo: "",
      organizer_name: user?.company_name || "",
      organizer_contact: "",
      bailiff_name: "",
      bailiff_info: "",
      withdrawal_instructions: "",
      custom_field_label: "",
      custom_field_required: false,
      background_type: "color",
      background_color: "#1e3a5f",
      background_image: "",
      background_video_url: "",
      accent_color: "#eab308",
      partner_carousel_enabled: false,
      partner_carousel_title: "Merci à nos partenaires",
      partner_logos: []
    }
  });

  const watchedValues = watch();

  // Combine form values with draw data for preview
  const previewDraw = {
    ...draw,
    ...watchedValues,
    prizes: draw?.prizes || [],
    participants: draw?.participants || []
  };

  useEffect(() => {
    if (!isNew) {
      loadDraw();
    }
  }, [id]);

  // Load email status when tab changes to results
  useEffect(() => {
    if (activeTab === "results" && draw?.status === "completed") {
      loadEmailStatus();
    }
  }, [activeTab, draw?.status, draw?.id]);

  // Countdown timer
  useEffect(() => {
    if (!draw) return;
    
    const calculateCountdown = (targetDate) => {
      if (!targetDate) return null;
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return { expired: true, text: "Terminé" };
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (days > 0) return { text: `${days}j ${hours}h ${minutes}m`, short: `${days}j ${hours}h` };
      if (hours > 0) return { text: `${hours}h ${minutes}m ${seconds}s`, short: `${hours}h ${minutes}m` };
      return { text: `${minutes}m ${seconds}s`, short: `${minutes}m ${seconds}s`, urgent: true };
    };
    
    const updateCountdown = () => {
      setCountdown({
        participation: calculateCountdown(draw.participation_deadline || draw.draw_date),
        draw: calculateCountdown(draw.draw_date)
      });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [draw?.participation_deadline, draw?.draw_date]);

  const loadDraw = async () => {
    try {
      const res = await drawsAPI.get(id);
      setDraw(res.data);
      reset(res.data);
      // Initialize date states
      setDates({
        start_date: res.data.start_date ? new Date(res.data.start_date) : null,
        draw_date: res.data.draw_date ? new Date(res.data.draw_date) : null,
        participation_deadline: res.data.participation_deadline ? new Date(res.data.participation_deadline) : null,
        publication_end_date: res.data.publication_end_date ? new Date(res.data.publication_end_date) : null
      });
      // Pre-fill certificate data with draw's controller info
      if (res.data.bailiff_name || res.data.bailiff_info) {
        setCertificateData(prev => ({
          ...prev,
          supervisor_type: res.data.has_bailiff ? "huissier" : "controleur",
          supervisor_name: res.data.bailiff_name || "",
          supervisor_role: res.data.bailiff_info || ""
        }));
      }
    } catch (error) {
      toast.error("Tirage non trouvé");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Load email status for completed draws
  const loadEmailStatus = async () => {
    if (!draw?.id || draw?.status !== "completed") return;
    setLoadingEmailStatus(true);
    try {
      const res = await drawsAPI.getEmailStatus(draw.id);
      setEmailStatus(res.data);
    } catch (error) {
      console.error("Failed to load email status:", error);
    } finally {
      setLoadingEmailStatus(false);
    }
  };

  // Save draw field updates
  const handleSave = async (updates) => {
    if (!draw?.id) return;
    try {
      const res = await drawsAPI.update(draw.id, updates);
      setDraw(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error("Save error:", error);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setValue("organizer_logo", res.data.url);
      toast.success("Logo uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUploadBeneficiaryLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingBeneficiaryLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setValue("beneficiary_logo", res.data.url);
      toast.success("Logo du bénéficiaire uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingBeneficiaryLogo(false);
    }
  };

  const handleUploadBackground = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingBg(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setValue("background_image", res.data.url);
      setValue("background_type", "image");
      toast.success("Image de fond uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingBg(false);
    }
  };

  const handleUploadPrizeImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPrizeImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setNewPrize(prev => ({ ...prev, image_url: res.data.url, media_type: "image" }));
      toast.success("Image du lot uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingPrizeImage(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      // Include dates in the submission
      const submitData = {
        ...data,
        start_date: dates.start_date?.toISOString() || null,
        draw_date: dates.draw_date?.toISOString() || null,
        participation_deadline: dates.participation_deadline?.toISOString() || null,
        publication_end_date: dates.publication_end_date?.toISOString() || null
      };
      
      if (isNew) {
        const res = await drawsAPI.create(submitData);
        toast.success("Tirage créé");
        navigate(`/draws/${res.data.id}/edit`);
      } else {
        await drawsAPI.update(id, submitData);
        toast.success("Tirage enregistré");
        loadDraw();
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!newParticipant.name.trim()) {
      toast.error("Nom requis");
      return;
    }
    if (!newParticipant.phone.trim()) {
      toast.error("Téléphone mobile obligatoire pour contacter le gagnant");
      return;
    }
    setAddingParticipant(true);
    try {
      await drawsAPI.addParticipant(id, {
        ...newParticipant,
        display_name: newParticipant.pseudo?.trim() || newParticipant.name.trim()
      });
      toast.success("Participant ajouté");
      setNewParticipant({ name: "", email: "", phone: "", custom_field_value: "", pseudo: "" });
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    try {
      await drawsAPI.removeParticipant(id, participantId);
      toast.success("Participant supprimé");
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdateParticipant = async (participantId, updates) => {
    try {
      await api.put(`/draws/${id}/participants/${participantId}`, updates);
      toast.success("Participant modifié");
      setEditingParticipant(null);
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  // CSV Import Functions
  const handleCsvFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvFileName(file.name);
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        toast.error("Le fichier doit contenir au moins une ligne d'en-tête et une ligne de données");
        return;
      }
      
      // First row is headers
      const headers = jsonData[0].map(h => String(h || "").trim());
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ""));
      
      setCsvColumns(headers);
      setCsvData(rows);
      
      // Auto-detect columns based on common names
      const autoMapping = { prenom: "none", nom: "none", email: "none", phone: "none", custom_field: "none", ticket_number: "none" };
      headers.forEach((header, idx) => {
        const lowerHeader = header.toLowerCase();
        if (lowerHeader.includes("prénom") || lowerHeader.includes("prenom") || lowerHeader === "firstname" || lowerHeader === "first_name") {
          autoMapping.prenom = String(idx);
        }
        if ((lowerHeader.includes("nom") && !lowerHeader.includes("prénom") && !lowerHeader.includes("prenom")) || lowerHeader === "lastname" || lowerHeader === "last_name" || lowerHeader === "name") {
          autoMapping.nom = String(idx);
        }
        if (lowerHeader.includes("email") || lowerHeader.includes("mail") || lowerHeader.includes("courriel")) {
          autoMapping.email = String(idx);
        }
        if (lowerHeader.includes("téléphone") || lowerHeader.includes("telephone") || lowerHeader.includes("phone") || lowerHeader.includes("mobile") || lowerHeader.includes("portable")) {
          autoMapping.phone = String(idx);
        }
        if (lowerHeader.includes("ticket") || (lowerHeader.includes("numéro") && !lowerHeader.includes("téléphone")) || (lowerHeader.includes("numero") && !lowerHeader.includes("telephone"))) {
          autoMapping.ticket_number = String(idx);
        }
      });
      
      setColumnMapping(autoMapping);
      setShowCsvMapping(true);
      
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Erreur lors de la lecture du fichier");
    }
    
    // Reset input
    e.target.value = "";
  };

  const handleImportCsv = async () => {
    if (!columnMapping.nom && !columnMapping.prenom) {
      toast.error("Veuillez mapper au moins le nom ou le prénom");
      return;
    }
    
    setImportingCsv(true);
    
    try {
      const participants = csvData.map(row => {
        const prenom = columnMapping.prenom ? String(row[parseInt(columnMapping.prenom)] || "").trim() : "";
        const nom = columnMapping.nom ? String(row[parseInt(columnMapping.nom)] || "").trim() : "";
        const fullName = [prenom, nom].filter(Boolean).join(" ") || "Participant";
        
        return {
          name: fullName,
          email: columnMapping.email ? String(row[parseInt(columnMapping.email)] || "").trim() : "",
          phone: columnMapping.phone ? String(row[parseInt(columnMapping.phone)] || "").trim() : "",
          custom_field_value: columnMapping.custom_field ? String(row[parseInt(columnMapping.custom_field)] || "").trim() : "",
          ticket_number: columnMapping.ticket_number ? String(row[parseInt(columnMapping.ticket_number)] || "").trim() : ""
        };
      }).filter(p => p.name && p.name !== "Participant");
      
      if (participants.length === 0) {
        toast.error("Aucun participant valide trouvé dans le fichier");
        setImportingCsv(false);
        return;
      }
      
      // Import participants via API
      let imported = 0;
      let errors = 0;
      
      for (const participant of participants) {
        try {
          await drawsAPI.addParticipant(id, participant);
          imported++;
        } catch (error) {
          errors++;
        }
      }
      
      toast.success(`${imported} participant(s) importé(s)${errors > 0 ? `, ${errors} erreur(s)` : ""}`);
      setShowCsvMapping(false);
      setCsvData([]);
      setCsvColumns([]);
      setCsvFileName("");
      setColumnMapping({ prenom: "", nom: "", email: "", phone: "", custom_field: "", ticket_number: "" });
      loadDraw();
      
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImportingCsv(false);
    }
  };

  const handleAddPrize = async () => {
    if (!newPrize.name.trim()) {
      toast.error("Nom du lot requis");
      return;
    }
    setAddingPrize(true);
    try {
      await drawsAPI.addPrize(id, newPrize);
      toast.success("Lot ajouté");
      setNewPrize({ 
        name: "", 
        description: "", 
        quantity: 1, 
        image_url: "",
        video_url: "",
        media_type: "image",
        partner_name: "",
        partner_logo_url: "",
        value: ""
      });
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAddingPrize(false);
    }
  };

  const handleRemovePrize = async (prizeId) => {
    try {
      await drawsAPI.removePrize(id, prizeId);
      toast.success("Lot supprimé");
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Duplicate a prize
  const handleDuplicatePrize = async (prize) => {
    try {
      const duplicatedPrize = {
        name: `${prize.name} (copie)`,
        description: prize.description || "",
        quantity: prize.quantity || 1,
        image_url: prize.image_url || "",
        video_url: prize.video_url || "",
        media_type: prize.media_type || "image",
        partner_name: prize.partner_name || "",
        partner_logo_url: prize.partner_logo_url || "",
        value: prize.value || ""
      };
      await drawsAPI.addPrize(id, duplicatedPrize);
      toast.success("Lot dupliqué");
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de la duplication");
    }
  };

  // Update an existing prize
  const handleUpdatePrize = async () => {
    if (!editingPrize || !editingPrize.name?.trim()) {
      toast.error("Le nom du lot est requis");
      return;
    }
    
    setSavingPrize(true);
    try {
      await drawsAPI.updatePrize(id, editingPrize.id, {
        name: editingPrize.name,
        description: editingPrize.description || "",
        quantity: editingPrize.quantity || 1,
        image_url: editingPrize.image_url || "",
        video_url: editingPrize.video_url || "",
        media_type: editingPrize.media_type || "image",
        partner_name: editingPrize.partner_name || "",
        partner_logo_url: editingPrize.partner_logo_url || "",
        value: editingPrize.value || "",
        pickup_instructions: editingPrize.pickup_instructions || ""
      });
      toast.success("Lot mis à jour");
      setEditingPrize(null);
      loadDraw();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingPrize(false);
    }
  };

  // Upload image for editing prize
  const handleUploadEditPrizeImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setEditingPrize(prev => ({ ...prev, image_url: res.data.url, media_type: "image" }));
      toast.success("Image uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
  };

  // Upload partner logo for editing prize
  const handleUploadEditPrizePartnerLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setEditingPrize(prev => ({ ...prev, partner_logo_url: res.data.url }));
      toast.success("Logo partenaire uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
  };

  const handleExecuteDraw = async () => {
    if (!draw?.participants?.length) {
      toast.error("Ajoutez des participants");
      return;
    }
    if (!draw?.prizes?.length) {
      toast.error("Ajoutez des lots");
      return;
    }
    
    try {
      await drawsAPI.execute(id);
      toast.success("Tirage effectué !");
      loadDraw();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du tirage");
    }
  };

  // Mark prize as withdrawn
  const handleToggleWithdrawal = async (participantId, prizeId, currentStatus) => {
    const key = `${participantId}_${prizeId}`;
    setUpdatingWithdrawal(key);
    try {
      await drawsAPI.updateWithdrawal(id, {
        participant_id: participantId,
        prize_id: prizeId,
        is_withdrawn: !currentStatus
      });
      
      // Update local state
      setDraw(prev => ({
        ...prev,
        results: prev.results.map(r => 
          r.participant?.id === participantId && r.prize_id === prizeId
            ? { ...r, is_withdrawn: !currentStatus, withdrawn_at: !currentStatus ? new Date().toISOString() : null }
            : r
        )
      }));
      
      toast.success(currentStatus ? "Lot marqué comme non retiré" : "Lot marqué comme retiré");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdatingWithdrawal(null);
    }
  };

  // Send reminder email to winner
  const handleSendReminder = async (participantId, prizeId, participantEmail) => {
    const key = `${participantId}_${prizeId}`;
    setSendingReminder(key);
    try {
      const deadlineDate = new Date();
      deadlineDate.setDate(deadlineDate.getDate() + 14); // 2 weeks from now
      const dateLimite = deadlineDate.toLocaleDateString("fr-FR", { 
        day: "numeric", 
        month: "long", 
        year: "numeric" 
      });

      await api.post(`/draws/${id}/send-reminder`, {
        participant_id: participantId,
        prize_id: prizeId,
        date_limite: dateLimite
      });
      
      toast.success(`Email de relance envoyé à ${participantEmail}`);
      loadDraw(); // Refresh to get updated reminder count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la relance");
    } finally {
      setSendingReminder(null);
    }
  };

  // Cancel unclaimed prize
  const handleCancelPrize = async (participantId, prizeId, action) => {
    const key = `${participantId}_${prizeId}`;
    setCancellingPrize(key);
    try {
      await api.post(`/draws/${id}/cancel-prize`, {
        participant_id: participantId,
        prize_id: prizeId,
        action: action // "cancel" or "reassign"
      });
      
      toast.success(action === "cancel" ? "Lot annulé" : "Lot marqué pour remise en jeu");
      loadDraw();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancellingPrize(null);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isCompleted = draw?.status === "completed";
  const isLive = draw?.status === "live";
  const isPublished = draw?.status === "published";
  const isDraft = draw?.status === "draft" || !draw?.status;
  
  // Check if participation deadline passed
  const participationDeadlinePassed = draw?.participation_deadline && new Date(draw.participation_deadline) < new Date();
  
  // Can add/modify participants only if not completed and deadline not passed
  const canModifyParticipants = !isCompleted && !participationDeadlinePassed;
  
  // Can modify prizes in all cases except when draw is completed
  const canModifyPrizes = !isCompleted;
  
  // Can modify general settings in draft/published states
  const canModifyDraw = !isLive && !isCompleted;

  // Status change handlers
  const handlePublish = async () => {
    if (!draw?.prizes?.length) {
      toast.error("Ajoutez au moins un lot avant de publier");
      return;
    }
    try {
      await drawsAPI.update(id, { status: "published" });
      loadDraw();
      toast.success("Tirage publié ! Les participants peuvent maintenant s'inscrire.");
    } catch (error) {
      toast.error("Erreur lors de la publication");
    }
  };

  const handleGoLive = async () => {
    try {
      await drawsAPI.update(id, { status: "live" });
      loadDraw();
      toast.success("Tirage en diffusion ! Vous ne pouvez plus modifier les lots.");
    } catch (error) {
      toast.error("Erreur lors de la mise en diffusion");
    }
  };

  const handleUnpublish = async () => {
    try {
      await drawsAPI.update(id, { status: "draft" });
      loadDraw();
      toast.success("Tirage repassé en brouillon");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col lg:flex-row">
      {/* Left Panel - Configuration */}
      <div className="w-full lg:w-1/2 border-r bg-card flex flex-col min-h-screen lg:min-h-0">
        {/* Status Bar */}
        {!isNew && (
          <div className={`px-4 py-2 flex items-center justify-between text-sm ${
            isCompleted ? "bg-green-100 dark:bg-green-950/50" :
            isLive ? "bg-orange-100 dark:bg-orange-950/50" :
            isPublished ? "bg-blue-100 dark:bg-blue-950/50" :
            "bg-gray-100 dark:bg-gray-800"
          }`}>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`${
                isCompleted ? "border-green-500 text-green-700 dark:text-green-400" :
                isLive ? "border-orange-500 text-orange-700 dark:text-orange-400" :
                isPublished ? "border-blue-500 text-blue-700 dark:text-blue-400" :
                "border-gray-500"
              }`}>
                {isCompleted ? "✓ Terminé" :
                 isLive ? "● En diffusion" :
                 isPublished ? "◉ Publié" :
                 "○ Brouillon"}
              </Badge>
              <span className="text-muted-foreground hidden sm:inline">
                {isCompleted ? "Le tirage est terminé, les résultats sont disponibles" :
                 isLive ? "En diffusion - Ajout de participants uniquement" :
                 isPublished ? "Les participants peuvent s'inscrire" :
                 "Configurez votre tirage puis publiez-le"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isDraft && (
                <Button size="sm" onClick={handlePublish} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs">
                  Publier
                </Button>
              )}
              {isPublished && (
                <>
                  <Button size="sm" variant="ghost" onClick={handleUnpublish} className="h-7 text-xs">
                    Repasser en brouillon
                  </Button>
                  <Button size="sm" onClick={handleGoLive} className="bg-orange-600 hover:bg-orange-700 h-7 text-xs">
                    Mettre en diffusion
                  </Button>
                </>
              )}
              {isLive && !isCompleted && (
                <Button 
                  size="sm" 
                  onClick={() => setPreviewMode("results")}
                  className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                >
                  <Play className="w-3 h-3 mr-1" />
                  Lancer le tirage
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Mini Dashboard - Stats Overview */}
        {!isNew && (
          <div className="border-b bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-3 py-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
              {/* Participants */}
              <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-2 py-1.5 shadow-sm">
                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <Users className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-bold text-sm">{(draw?.participants?.length || 0) + (draw?.manual_participant_count || 0)}</p>
                  <p className="text-muted-foreground text-[10px]">Participants</p>
                </div>
              </div>
              
              {/* Lots */}
              <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-2 py-1.5 shadow-sm">
                <div className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                  <Gift className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="font-bold text-sm">{draw?.prizes?.length || 0}</p>
                  <p className="text-muted-foreground text-[10px]">Lots</p>
                </div>
              </div>
              
              {/* Lots to give */}
              <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-2 py-1.5 shadow-sm">
                <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <Package className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-bold text-sm">{draw?.prizes?.reduce((sum, p) => sum + (p.quantity || 1), 0) || 0}</p>
                  <p className="text-muted-foreground text-[10px]">À remettre</p>
                </div>
              </div>
              
              {/* Date tirage */}
              <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-2 py-1.5 shadow-sm">
                <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Trophy className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-bold text-sm">
                    {draw?.draw_date ? new Date(draw.draw_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : "N/A"}
                  </p>
                  <p className="text-muted-foreground text-[10px]">Tirage</p>
                </div>
              </div>
              
              {/* Countdown participation */}
              <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 shadow-sm ${
                countdown.participation?.expired 
                  ? "bg-red-100 dark:bg-red-900/50" 
                  : countdown.participation?.urgent 
                    ? "bg-orange-100 dark:bg-orange-900/50" 
                    : "bg-white dark:bg-slate-800"
              }`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  countdown.participation?.expired 
                    ? "bg-red-200 dark:bg-red-800" 
                    : countdown.participation?.urgent 
                      ? "bg-orange-200 dark:bg-orange-800" 
                      : "bg-cyan-100 dark:bg-cyan-900"
                }`}>
                  <Clock className={`w-3.5 h-3.5 ${
                    countdown.participation?.expired 
                      ? "text-red-600 dark:text-red-400" 
                      : countdown.participation?.urgent 
                        ? "text-orange-600 dark:text-orange-400" 
                        : "text-cyan-600 dark:text-cyan-400"
                  }`} />
                </div>
                <div>
                  <p className={`font-bold text-sm ${countdown.participation?.urgent ? "text-orange-600" : ""}`}>
                    {countdown.participation?.short || "—"}
                  </p>
                  <p className="text-muted-foreground text-[10px]">Inscriptions</p>
                </div>
              </div>
              
              {/* Countdown tirage */}
              <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 shadow-sm ${
                countdown.draw?.expired 
                  ? "bg-green-100 dark:bg-green-900/50" 
                  : countdown.draw?.urgent 
                    ? "bg-red-100 dark:bg-red-900/50" 
                    : "bg-white dark:bg-slate-800"
              }`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  countdown.draw?.expired 
                    ? "bg-green-200 dark:bg-green-800" 
                    : countdown.draw?.urgent 
                      ? "bg-red-200 dark:bg-red-800" 
                      : "bg-pink-100 dark:bg-pink-900"
                }`}>
                  <Play className={`w-3.5 h-3.5 ${
                    countdown.draw?.expired 
                      ? "text-green-600 dark:text-green-400" 
                      : countdown.draw?.urgent 
                        ? "text-red-600 dark:text-red-400" 
                        : "text-pink-600 dark:text-pink-400"
                  }`} />
                </div>
                <div>
                  <p className={`font-bold text-sm ${countdown.draw?.urgent ? "text-red-600" : ""}`}>
                    {countdown.draw?.short || "—"}
                  </p>
                  <p className="text-muted-foreground text-[10px]">Avant tirage</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="border-b p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
                  {isNew ? "Nouveau tirage" : draw?.title}
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    {isNew ? "Créez votre tirage" : draw?.organizer_name}
                  </p>
                  {/* Auto-save indicator */}
                  {!isNew && canModifyDraw && (
                    <span className="text-xs text-green-600 flex items-center gap-1 bg-green-50 dark:bg-green-950/30 px-2 py-0.5 rounded-full">
                      <Check className="w-3 h-3" />
                      Sauvegarde auto
                    </span>
                  )}
                  {isLive && (
                    <span className="text-xs text-orange-600 flex items-center gap-1 bg-orange-50 dark:bg-orange-950/30 px-2 py-0.5 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      Mode lecture seule
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Guide Button */}
              <DrawGuide />
              
              {!isNew && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(`/draw/${id}/register`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Inscription</span>
                  </Button>
                  <Button 
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-pink-500"
                    onClick={() => window.open(`/draw/${id}/show`, '_blank')}
                    title="Prévisualiser la page SHOW"
                  >
                    <Monitor className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">SHOW</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex-1 overflow-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            {/* Mobile: Dropdown Select */}
            <div className="sm:hidden border-b p-3">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full" data-testid="mobile-tab-select">
                  <SelectValue>
                    {activeTab === "info" && "Informations"}
                    {activeTab === "participants" && `Participants (${draw?.participants?.length || 0})`}
                    {activeTab === "prizes" && `Lots (${draw?.prizes?.length || 0})`}
                    {activeTab === "results" && `Résultats (${draw?.results?.length || 0})`}
                    {activeTab === "share" && "Partage"}
                    {activeTab === "design" && "Design"}
                    {activeTab === "photos" && "Mur Photos"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">
                    <span className="flex items-center gap-2">Informations</span>
                  </SelectItem>
                  {!isNew && (
                    <>
                      <SelectItem value="participants">
                        <span className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Participants ({draw?.participants?.length || 0})
                        </span>
                      </SelectItem>
                      <SelectItem value="prizes">
                        <span className="flex items-center gap-2">
                          <Gift className="w-4 h-4" />
                          Lots ({draw?.prizes?.length || 0})
                        </span>
                      </SelectItem>
                      <SelectItem value="share">
                        <span className="flex items-center gap-2">
                          <Share2 className="w-4 h-4" />
                          Partage
                        </span>
                      </SelectItem>
                      <SelectItem value="design">
                        <span className="flex items-center gap-2">
                          <Palette className="w-4 h-4" />
                          Design
                        </span>
                      </SelectItem>
                      <SelectItem value="photos">
                        <span className="flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          Mur Photos
                        </span>
                      </SelectItem>
                      {isCompleted && (
                        <SelectItem value="results">
                          <span className="flex items-center gap-2 text-amber-600">
                            <Trophy className="w-4 h-4" />
                            Résultats ({draw?.results?.length || 0})
                          </span>
                        </SelectItem>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop: Traditional Tabs */}
            <TabsList className="hidden sm:flex w-full justify-start rounded-none border-b bg-transparent p-0">
              <TabsTrigger value="info" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                Informations
              </TabsTrigger>
              {!isNew && (
                <>
                  <TabsTrigger value="participants" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Participants ({draw?.participants?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="prizes" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Lots ({draw?.prizes?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="share" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    <Share2 className="w-4 h-4 mr-1" />
                    Partage
                  </TabsTrigger>
                  <TabsTrigger value="design" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Design
                  </TabsTrigger>
                  <TabsTrigger value="photos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    <Camera className="w-4 h-4 mr-1" />
                    Mur Photos
                  </TabsTrigger>
                  {isCompleted && (
                    <TabsTrigger value="results" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-amber-600">
                      <Trophy className="w-4 h-4 mr-1" />
                      Résultats ({draw?.results?.length || 0})
                    </TabsTrigger>
                  )}
                </>
              )}
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="flex-1 p-4 overflow-auto">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Titre *</Label>
                  <Input
                    {...register("title", { required: "Titre requis" })}
                    placeholder="Grand Jeu Concours"
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground">Description</Label>
                  <Textarea
                    {...register("description")}
                    placeholder="Description du tirage..."
                    className="bg-background"
                    rows={2}
                  />
                </div>

                {/* Dates Section */}
                <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-foreground flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-blue-500" />
                      Dates et horaires
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Start Date */}
                      <div className="space-y-2">
                        <Label className="text-foreground text-sm">Début des inscriptions</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dates.start_date ? format(dates.start_date, "dd/MM/yyyy HH:mm", { locale: fr }) : "Sélectionner"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={dates.start_date}
                              onSelect={(date) => setDates(prev => ({ ...prev, start_date: date }))}
                              locale={fr}
                            />
                            <div className="p-3 border-t">
                              <Label className="text-xs">Heure</Label>
                              <Input
                                type="time"
                                className="mt-1"
                                value={dates.start_date ? format(dates.start_date, "HH:mm") : "00:00"}
                                onChange={(e) => {
                                  const [hours, minutes] = e.target.value.split(':');
                                  const newDate = dates.start_date ? new Date(dates.start_date) : new Date();
                                  newDate.setHours(parseInt(hours), parseInt(minutes));
                                  setDates(prev => ({ ...prev, start_date: newDate }));
                                }}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Participation Deadline */}
                      <div className="space-y-2">
                        <Label className="text-foreground text-sm">Fin des inscriptions</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dates.participation_deadline ? format(dates.participation_deadline, "dd/MM/yyyy HH:mm", { locale: fr }) : "Sélectionner"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={dates.participation_deadline}
                              onSelect={(date) => setDates(prev => ({ ...prev, participation_deadline: date }))}
                              locale={fr}
                            />
                            <div className="p-3 border-t">
                              <Label className="text-xs">Heure</Label>
                              <Input
                                type="time"
                                className="mt-1"
                                value={dates.participation_deadline ? format(dates.participation_deadline, "HH:mm") : "00:00"}
                                onChange={(e) => {
                                  const [hours, minutes] = e.target.value.split(':');
                                  const newDate = dates.participation_deadline ? new Date(dates.participation_deadline) : new Date();
                                  newDate.setHours(parseInt(hours), parseInt(minutes));
                                  setDates(prev => ({ ...prev, participation_deadline: newDate }));
                                }}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Draw Date */}
                      <div className="space-y-2">
                        <Label className="text-foreground text-sm">Date du tirage</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dates.draw_date ? format(dates.draw_date, "dd/MM/yyyy HH:mm", { locale: fr }) : "Sélectionner"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={dates.draw_date}
                              onSelect={(date) => setDates(prev => ({ ...prev, draw_date: date }))}
                              locale={fr}
                            />
                            <div className="p-3 border-t">
                              <Label className="text-xs">Heure</Label>
                              <Input
                                type="time"
                                className="mt-1"
                                value={dates.draw_date ? format(dates.draw_date, "HH:mm") : "00:00"}
                                onChange={(e) => {
                                  const [hours, minutes] = e.target.value.split(':');
                                  const newDate = dates.draw_date ? new Date(dates.draw_date) : new Date();
                                  newDate.setHours(parseInt(hours), parseInt(minutes));
                                  setDates(prev => ({ ...prev, draw_date: newDate }));
                                }}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Publication End Date */}
                      <div className="space-y-2">
                        <Label className="text-foreground text-sm">Fin de publication</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dates.publication_end_date ? format(dates.publication_end_date, "dd/MM/yyyy HH:mm", { locale: fr }) : "Sélectionner"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={dates.publication_end_date}
                              onSelect={(date) => setDates(prev => ({ ...prev, publication_end_date: date }))}
                              locale={fr}
                            />
                            <div className="p-3 border-t">
                              <Label className="text-xs">Heure</Label>
                              <Input
                                type="time"
                                className="mt-1"
                                value={dates.publication_end_date ? format(dates.publication_end_date, "HH:mm") : "00:00"}
                                onChange={(e) => {
                                  const [hours, minutes] = e.target.value.split(':');
                                  const newDate = dates.publication_end_date ? new Date(dates.publication_end_date) : new Date();
                                  newDate.setHours(parseInt(hours), parseInt(minutes));
                                  setDates(prev => ({ ...prev, publication_end_date: newDate }));
                                }}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Organisateur *</Label>
                    <Input
                      {...register("organizer_name", { required: true })}
                      placeholder="Nom de l'entreprise"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Contact</Label>
                    <Input
                      {...register("organizer_contact")}
                      placeholder="Email ou téléphone"
                      className="bg-background"
                    />
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label className="text-foreground">Logo</Label>
                  <div className="flex items-center gap-4">
                    {watchedValues.organizer_logo ? (
                      <img 
                        src={getImageUrl(watchedValues.organizer_logo)} 
                        alt="Logo" 
                        className="w-16 h-16 object-contain rounded-lg bg-muted"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                        <Image className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <input
                      type="file"
                      ref={logoInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={handleUploadLogo}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Uploader
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Bénéficiaire Section */}
                <Card className="bg-pink-50 dark:bg-pink-950/20 border-pink-200 dark:border-pink-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-foreground flex items-center gap-2">
                      <Heart className="w-4 h-4 text-pink-500" />
                      Bénéficiaire (optionnel)
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Si le tirage est organisé au profit d'une association ou cause
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Nom du bénéficiaire</Label>
                      <Input
                        {...register("beneficiary_name")}
                        placeholder="Ex: Association Petits Princes"
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Logo du bénéficiaire</Label>
                      <div className="flex items-center gap-4">
                        {watchedValues.beneficiary_logo ? (
                          <img 
                            src={getImageUrl(watchedValues.beneficiary_logo)} 
                            alt="Logo bénéficiaire" 
                            className="w-16 h-16 object-contain rounded-lg bg-muted"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                            <Heart className="w-6 h-6 text-pink-400" />
                          </div>
                        )}
                        <input
                          type="file"
                          ref={beneficiaryLogoRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleUploadBeneficiaryLogo}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => beneficiaryLogoRef.current?.click()}
                          disabled={uploadingBeneficiaryLogo}
                        >
                          {uploadingBeneficiaryLogo ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Uploader
                            </>
                          )}
                        </Button>
                        {watchedValues.beneficiary_logo && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setValue("beneficiary_logo", "")}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contrôle du tirage */}
                <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-foreground flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      Personne en charge du contrôle (optionnel)
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Si le tirage est supervisé par un huissier ou un contrôleur
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          {...register("has_bailiff")}
                          className="rounded"
                        />
                        Contrôle par huissier de justice
                      </label>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          {watchedValues.has_bailiff ? "Nom de l'huissier" : "Nom du contrôleur"}
                        </Label>
                        <Input
                          {...register("bailiff_name")}
                          placeholder={watchedValues.has_bailiff ? "Me Jean Dupont" : "Nom de la personne"}
                          className="bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          {watchedValues.has_bailiff ? "Étude / Informations" : "Fonction / Rôle"}
                        </Label>
                        <Input
                          {...register("bailiff_info")}
                          placeholder={watchedValues.has_bailiff ? "Étude de Me Dupont, Paris" : "Président, Responsable..."}
                          className="bg-background"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {isNew ? "Créer le tirage" : "Enregistrer"}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Participants Tab */}
            <TabsContent value="participants" className="flex-1 p-4 overflow-auto">
              {/* Warning if deadline passed */}
              {participationDeadlinePassed && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Date limite de participation dépassée</span>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-500 mt-1">
                    Il n'est plus possible d'ajouter de nouveaux participants.
                  </p>
                </div>
              )}
              
              {/* Custom Field Configuration */}
              <Card className={`mb-4 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800 ${isLive ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-foreground flex items-center gap-2">
                    <Tag className="w-4 h-4 text-purple-500" />
                    Champ personnalisé
                    {isLive && <Badge variant="outline" className="text-xs">Lecture seule</Badge>}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Ajoutez un champ additionnel pour vos participants (ex: N° de ticket)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    {...register("custom_field_label")}
                    placeholder="Nom du champ (ex: N° de ticket)"
                    className="bg-background"
                    disabled={isLive}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        {...register("custom_field_required")}
                        className="rounded"
                        disabled={isLive}
                      />
                      Champ obligatoire
                    </label>
                    {!isLive && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                        Sauver
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Add Participant Form */}
              {canModifyParticipants && (
                <Card className="mb-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Ajouter un participant</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        placeholder="Nom complet *"
                        value={newParticipant.name}
                        onChange={(e) => setNewParticipant({...newParticipant, name: e.target.value})}
                        className="bg-background"
                      />
                      <Input
                        placeholder="Pseudo (optionnel - affiché à la place du nom)"
                        value={newParticipant.pseudo}
                        onChange={(e) => setNewParticipant({...newParticipant, pseudo: e.target.value})}
                        className="bg-background"
                      />
                      <Input
                        placeholder="Téléphone mobile *"
                        value={newParticipant.phone}
                        onChange={(e) => setNewParticipant({...newParticipant, phone: e.target.value})}
                        className="bg-background"
                      />
                      <Input
                        placeholder="Email"
                        value={newParticipant.email}
                        onChange={(e) => setNewParticipant({...newParticipant, email: e.target.value})}
                        className="bg-background"
                      />
                      {draw?.custom_field_label && (
                        <Input
                          placeholder={draw.custom_field_label}
                          value={newParticipant.custom_field_value}
                          onChange={(e) => setNewParticipant({...newParticipant, custom_field_value: e.target.value})}
                          className="bg-background sm:col-span-2"
                        />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      * Le téléphone est obligatoire pour contacter les gagnants. Le pseudo sera affiché publiquement à la place du nom si renseigné.
                    </p>
                    <Button
                      className="mt-3 w-full"
                      onClick={handleAddParticipant}
                      disabled={addingParticipant}
                    >
                      {addingParticipant ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Ajouter participant
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* CSV Import Section */}
              {!isCompleted && (
                <Card className="mb-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-foreground flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      Importer une liste (CSV/Excel)
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Importez vos participants depuis un fichier CSV ou Excel et mappez les colonnes
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <input
                      type="file"
                      ref={csvInputRef}
                      className="hidden"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleCsvFileSelect}
                    />
                    <Button
                      variant="outline"
                      className="w-full border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
                      onClick={() => csvInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Sélectionner un fichier CSV/Excel
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Formats acceptés : .csv, .xlsx, .xls
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Manual Participant Count Bonus */}
              {!isCompleted && (
                <Card className="mb-4 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-foreground flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      Compteur manuel de participants
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Ajoutez un nombre de participants supplémentaires pour l'affichage sur l'écran SHOW (ex: participants en magasin)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Participants additionnels</Label>
                        <Input
                          type="number"
                          min="0"
                          defaultValue={draw?.manual_participant_count || 0}
                          onBlur={async (e) => {
                            const value = parseInt(e.target.value) || 0;
                            if (value !== (draw?.manual_participant_count || 0)) {
                              try {
                                await drawsAPI.update(id, { manual_participant_count: value });
                                loadDraw();
                                toast.success("Compteur mis à jour");
                              } catch (error) {
                                toast.error("Erreur lors de la mise à jour");
                              }
                            }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              const value = parseInt(e.target.value) || 0;
                              if (value !== (draw?.manual_participant_count || 0)) {
                                try {
                                  await drawsAPI.update(id, { manual_participant_count: value });
                                  loadDraw();
                                  toast.success("Compteur mis à jour");
                                } catch (error) {
                                  toast.error("Erreur lors de la mise à jour");
                                }
                              }
                            }
                          }}
                          className="bg-background"
                        />
                      </div>
                      <div className="text-center px-4 py-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                          {(draw?.participants?.length || 0) + (draw?.manual_participant_count || 0)}
                        </p>
                        <p className="text-xs text-purple-600 dark:text-purple-400">Total affiché</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Ce nombre s'ajoute aux {draw?.participants?.length || 0} participants inscrits. Appuyez sur Entrée ou quittez le champ pour sauvegarder.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Participants Table */}
              <div className="bg-card rounded-lg border overflow-hidden">
                {/* Search Bar */}
                <div className="p-3 border-b bg-muted/30">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher par nom, pseudo ou téléphone..."
                      value={participantSearch}
                      onChange={(e) => setParticipantSearch(e.target.value)}
                      className="pl-10 bg-background"
                    />
                  </div>
                </div>
                
                {draw?.participants?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">Réf.</th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">Nom / Pseudo</th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">Téléphone *</th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">Email</th>
                          {draw?.custom_field_label && (
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">{draw.custom_field_label}</th>
                          )}
                          {canModifyParticipants && <th className="px-3 py-2 w-10"></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {draw.participants
                          .filter(p => {
                            if (!participantSearch.trim()) return true;
                            const search = participantSearch.toLowerCase();
                            return (
                              (p.name || "").toLowerCase().includes(search) ||
                              (p.display_name || "").toLowerCase().includes(search) ||
                              (p.pseudo || "").toLowerCase().includes(search) ||
                              (p.phone || "").includes(search)
                            );
                          })
                          .map((p, index) => (
                          <tr key={p.id} className="hover:bg-muted/30 group">
                            <td className="px-3 py-2">
                              <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                {p.reference || `T${String(index + 1).padStart(6, '0')}`}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              {editingParticipant?.id === p.id ? (
                                <div className="space-y-1">
                                  <Input
                                    value={editingParticipant.name}
                                    onChange={(e) => setEditingParticipant({...editingParticipant, name: e.target.value})}
                                    className="h-8 text-sm"
                                    placeholder="Nom complet"
                                  />
                                  <Input
                                    value={editingParticipant.pseudo || ""}
                                    onChange={(e) => setEditingParticipant({...editingParticipant, pseudo: e.target.value, display_name: e.target.value || editingParticipant.name})}
                                    className="h-8 text-sm"
                                    placeholder="Pseudo (optionnel)"
                                  />
                                </div>
                              ) : (
                                <div>
                                  <span className="font-medium">{p.display_name || p.pseudo || p.name}</span>
                                  {(p.pseudo || p.display_name) && p.display_name !== p.name && (
                                    <p className="text-xs text-muted-foreground">({p.name})</p>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {editingParticipant?.id === p.id ? (
                                <Input
                                  value={editingParticipant.phone || ""}
                                  onChange={(e) => setEditingParticipant({...editingParticipant, phone: e.target.value})}
                                  className="h-8 text-sm"
                                  placeholder="Téléphone *"
                                />
                              ) : (
                                <span className={`${p.phone ? "text-foreground" : "text-red-500"}`}>
                                  {p.phone || "⚠️ Manquant"}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {editingParticipant?.id === p.id ? (
                                <Input
                                  value={editingParticipant.email || ""}
                                  onChange={(e) => setEditingParticipant({...editingParticipant, email: e.target.value})}
                                  className="h-8 text-sm"
                                  placeholder="Email"
                                />
                              ) : (
                                <span className="text-muted-foreground">{p.email || "-"}</span>
                              )}
                            </td>
                            {draw?.custom_field_label && (
                              <td className="px-3 py-2">
                                {editingParticipant?.id === p.id ? (
                                  <Input
                                    value={editingParticipant.custom_field_value || ""}
                                    onChange={(e) => setEditingParticipant({...editingParticipant, custom_field_value: e.target.value})}
                                    className="h-8 text-sm"
                                  />
                                ) : (
                                  <span className="text-muted-foreground">{p.custom_field_value || "-"}</span>
                                )}
                              </td>
                            )}
                            {canModifyParticipants && (
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                  {editingParticipant?.id === p.id ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-green-600"
                                        onClick={() => handleUpdateParticipant(p.id, editingParticipant)}
                                      >
                                        <Check className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => setEditingParticipant(null)}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 opacity-0 group-hover:opacity-100"
                                        onClick={() => setEditingParticipant({ ...p })}
                                      >
                                        <Pen className="w-3 h-3 text-blue-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 opacity-0 group-hover:opacity-100"
                                        onClick={() => handleRemoveParticipant(p.id)}
                                      >
                                        <Trash2 className="w-3 h-3 text-destructive" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    Aucun participant
                  </p>
                )}
              </div>
            </TabsContent>

            {/* Prizes Tab */}
            <TabsContent value="prizes" className="flex-1 p-4 overflow-auto">
              {/* Warning if completed */}
              {isCompleted && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <Trophy className="w-5 h-5" />
                    <span className="font-medium">Tirage terminé</span>
                  </div>
                  <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                    Les lots ne peuvent plus être modifiés car le tirage est terminé.
                  </p>
                </div>
              )}
              
              {canModifyPrizes && (
                <Card className="mb-4 border-primary/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gift className="w-5 h-5 text-primary" />
                      Ajouter un lot
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Name and Quantity */}
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        placeholder="Nom du lot *"
                        value={newPrize.name}
                        onChange={(e) => setNewPrize({...newPrize, name: e.target.value})}
                        className="col-span-2 bg-background"
                      />
                      <Input
                        type="number"
                        placeholder="Qté"
                        min={1}
                        value={newPrize.quantity}
                        onChange={(e) => setNewPrize({...newPrize, quantity: parseInt(e.target.value) || 1})}
                        className="bg-background"
                      />
                    </div>
                    
                    {/* Description */}
                    <Textarea
                      placeholder="Description du lot (optionnel)"
                      value={newPrize.description}
                      onChange={(e) => setNewPrize({...newPrize, description: e.target.value})}
                      className="bg-background"
                      rows={2}
                    />
                    
                    {/* Value */}
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <Input
                        placeholder="Valeur estimée (ex: 500€)"
                        value={newPrize.value}
                        onChange={(e) => setNewPrize({...newPrize, value: e.target.value})}
                        className="bg-background"
                      />
                    </div>

                    {/* Media Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Média</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={newPrize.media_type === "image" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setNewPrize({...newPrize, media_type: "image", video_url: ""})}
                        >
                          <Image className="w-4 h-4 mr-1" />
                          Image
                        </Button>
                        <Button
                          type="button"
                          variant={newPrize.media_type === "video" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setNewPrize({...newPrize, media_type: "video", image_url: ""})}
                        >
                          <Video className="w-4 h-4 mr-1" />
                          Vidéo YouTube
                        </Button>
                      </div>
                    </div>

                    {/* Image Upload */}
                    {newPrize.media_type === "image" && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          {newPrize.image_url ? (
                            <img 
                              src={getImageUrl(newPrize.image_url)} 
                              alt="Lot" 
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center">
                              <Image className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1">
                            <input
                              type="file"
                              ref={prizeImageRef}
                              className="hidden"
                              accept="image/*"
                              onChange={handleUploadPrizeImage}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => prizeImageRef.current?.click()}
                              disabled={uploadingPrizeImage}
                            >
                              {uploadingPrizeImage ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-1" />
                                  Upload image
                                </>
                              )}
                            </Button>
                            <p className="text-xs text-muted-foreground mt-1">ou coller une URL</p>
                            <Input
                              placeholder="https://..."
                              value={newPrize.image_url}
                              onChange={(e) => setNewPrize({...newPrize, image_url: e.target.value})}
                              className="mt-1 h-8 text-xs bg-background"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Video URL */}
                    {newPrize.media_type === "video" && (
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">URL YouTube</Label>
                        <Input
                          placeholder="https://www.youtube.com/watch?v=..."
                          value={newPrize.video_url}
                          onChange={(e) => setNewPrize({...newPrize, video_url: e.target.value})}
                          className="bg-background"
                        />
                        <p className="text-xs text-muted-foreground">
                          La vidéo sera lue automatiquement sur la page SHOW
                        </p>
                      </div>
                    )}

                    {/* Partner Info */}
                    <div className="pt-2 border-t">
                      <Label className="text-xs text-muted-foreground mb-2 block">Partenaire (optionnel)</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Nom du partenaire"
                          value={newPrize.partner_name}
                          onChange={(e) => setNewPrize({...newPrize, partner_name: e.target.value})}
                          className="bg-background"
                        />
                        <div className="flex items-center gap-2">
                          {newPrize.partner_logo_url ? (
                            <div className="relative">
                              <img 
                                src={getImageUrl(newPrize.partner_logo_url)} 
                                alt="Logo partenaire"
                                className="h-9 w-9 object-contain rounded border bg-white p-0.5"
                              />
                              <button
                                type="button"
                                onClick={() => setNewPrize({...newPrize, partner_logo_url: ""})}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white rounded-full flex items-center justify-center"
                              >
                                <X className="w-2 h-2" />
                              </button>
                            </div>
                          ) : (
                            <label className="flex-1 h-9 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors bg-background">
                              <Upload className="w-4 h-4 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Logo partenaire</span>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  try {
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    const res = await api.post("/upload", formData, {
                                      headers: { "Content-Type": "multipart/form-data" }
                                    });
                                    setNewPrize({...newPrize, partner_logo_url: res.data.url});
                                    toast.success("Logo ajouté");
                                  } catch (error) {
                                    toast.error("Erreur lors de l'upload");
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Pickup Instructions */}
                    <div className="pt-2 border-t">
                      <Label className="text-xs text-muted-foreground mb-2 block">Consignes de retrait</Label>
                      <Input
                        placeholder="Ex: Retrait en boutique sur présentation d'une pièce d'identité"
                        value={newPrize.pickup_instructions || ""}
                        onChange={(e) => setNewPrize({...newPrize, pickup_instructions: e.target.value})}
                        className="bg-background"
                      />
                    </div>

                    <Button
                      className="w-full"
                      onClick={handleAddPrize}
                      disabled={addingPrize || !newPrize.name.trim()}
                    >
                      {addingPrize ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Ajouter ce lot
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Prizes List */}
              <div className="space-y-3">
                {draw?.prizes?.map((prize, index) => (
                  <Card key={prize.id} className="overflow-hidden group hover:border-primary/30 transition-colors">
                    <div className="flex">
                      {/* Prize Image/Video Preview */}
                      <div className="w-24 h-24 flex-shrink-0 bg-muted relative">
                        {prize.image_url ? (
                          <img 
                            src={getImageUrl(prize.image_url)} 
                            alt={prize.name}
                            className="w-full h-full object-cover"
                          />
                        ) : prize.video_url ? (
                          <div className="w-full h-full bg-gradient-to-br from-red-500/20 to-red-600/20 flex items-center justify-center">
                            <Video className="w-8 h-8 text-red-500" />
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Gift className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        {/* Rank Badge */}
                        <div 
                          className="absolute top-1 left-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: watchedValues.accent_color || '#eab308' }}
                        >
                          {index + 1}
                        </div>
                      </div>
                      
                      {/* Prize Info */}
                      <div className="flex-1 p-3 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-sm">{prize.name}</h4>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">x{prize.quantity}</Badge>
                              {prize.value && (
                                <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                                  {prize.value}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {prize.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                              {prize.description}
                            </p>
                          )}
                          {prize.pickup_instructions && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                              <Package className="w-3 h-3" />
                              {prize.pickup_instructions}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          {prize.partner_name ? (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              {prize.partner_logo_url && (
                                <img src={getImageUrl(prize.partner_logo_url)} alt="" className="w-4 h-4 rounded" />
                              )}
                              <span>par {prize.partner_name}</span>
                            </div>
                          ) : (
                            <span />
                          )}
                          {!isCompleted && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {/* Edit button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => setEditingPrize({...prize})}
                                title="Modifier ce lot"
                              >
                                <Pen className="w-4 h-4 text-green-500" />
                              </Button>
                              {/* Duplicate button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleDuplicatePrize(prize)}
                                title="Dupliquer ce lot"
                              >
                                <Copy className="w-4 h-4 text-blue-500" />
                              </Button>
                              {/* Delete button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleRemovePrize(prize.id)}
                                title="Supprimer ce lot"
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
                {draw?.prizes?.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Gift className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Aucun lot ajouté</p>
                    <p className="text-xs">Ajoutez des lots pour votre tirage</p>
                  </div>
                )}
              </div>

              {/* Consolation Prize Section */}
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium flex items-center gap-2">
                      <Gift className="w-4 h-4 text-purple-500" />
                      Lot de consolation
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Pour les participants qui n'ont pas gagné
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draw?.consolation_enabled || false}
                      onChange={(e) => handleSave({ consolation_enabled: e.target.checked })}
                      className="rounded border-input"
                    />
                    <span className="text-sm">Activer</span>
                  </label>
                </div>

                {draw?.consolation_enabled && (
                  <div className="space-y-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4">
                    {/* Type Selection */}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={draw?.consolation_type !== "prize" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSave({ consolation_type: "message" })}
                        className="flex-1"
                      >
                        Message
                      </Button>
                      <Button
                        type="button"
                        variant={draw?.consolation_type === "prize" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSave({ consolation_type: "prize" })}
                        className="flex-1"
                      >
                        Code promo
                      </Button>
                    </div>

                    {/* Message Option */}
                    {draw?.consolation_type !== "prize" && (
                      <div className="space-y-2">
                        <Label className="text-xs">Message de consolation</Label>
                        <textarea
                          className="w-full p-3 rounded-lg border text-sm min-h-[80px] bg-background"
                          placeholder="Merci pour votre participation ! La chance sourira peut-être la prochaine fois..."
                          value={draw?.consolation_message || ""}
                          onChange={(e) => handleSave({ consolation_message: e.target.value })}
                        />
                      </div>
                    )}

                    {/* Prize Option */}
                    {draw?.consolation_type === "prize" && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-xs">Nom du lot</Label>
                          <Input
                            placeholder="Ex: -10% sur votre prochain achat"
                            value={draw?.consolation_prize_name || ""}
                            onChange={(e) => handleSave({ consolation_prize_name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Code promo</Label>
                          <Input
                            placeholder="Ex: MERCI10"
                            value={draw?.consolation_prize_code || ""}
                            onChange={(e) => handleSave({ consolation_prize_code: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Validité</Label>
                          <Input
                            placeholder="Ex: Valable jusqu'au 31/12/2026"
                            value={draw?.consolation_prize_validity || ""}
                            onChange={(e) => handleSave({ consolation_prize_validity: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Execute Draw Button */}
              {!isCompleted && draw?.participants?.length > 0 && draw?.prizes?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <Button
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                    onClick={handleExecuteDraw}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Lancer le tirage
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Results Tab - Only visible when completed */}
            {isCompleted && (
              <TabsContent value="results" className="flex-1 p-4 overflow-auto">
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        Résultats du tirage
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Gérez les retraits de lots par les gagnants
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {draw?.results?.length > 0 && (
                        <>
                          {/* Broadcast Results Button */}
                          <Button
                            variant={draw?.results_broadcast ? "default" : "outline"}
                            size="sm"
                            className={draw?.results_broadcast ? "bg-green-600 hover:bg-green-700" : "border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-950"}
                            onClick={async () => {
                              try {
                                const res = await drawsAPI.broadcast(draw.id);
                                setDraw(prev => ({ ...prev, results_broadcast: res.data.results_broadcast }));
                                toast.success(res.data.message);
                              } catch (error) {
                                toast.error("Erreur lors de la diffusion");
                              }
                            }}
                          >
                            {draw?.results_broadcast ? (
                              <>
                                <Monitor className="w-4 h-4 mr-2" />
                                Diffusion active
                              </>
                            ) : (
                              <>
                                <Monitor className="w-4 h-4 mr-2" />
                                Diffuser les résultats
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Export winners to Excel
                              const exportData = draw.results.map((result, index) => ({
                                "Rang": index + 1,
                                "Nom du gagnant": result.participant?.name || "N/A",
                                "Email": result.participant?.email || "N/A",
                                "Téléphone": result.participant?.phone || "N/A",
                                "Lot gagné": result.prize?.name || result.prize_name || "N/A",
                                "Valeur": result.prize?.value || "",
                                "Partenaire": result.prize?.partner_name || "",
                                "Statut": result.is_cancelled ? "Annulé" : result.is_withdrawn ? "Retiré" : "En attente",
                                "Date de retrait": result.withdrawn_at ? new Date(result.withdrawn_at).toLocaleDateString('fr-FR') : "",
                                "N° Ticket": result.participant?.ticket_number || ""
                              }));
                              
                              const ws = XLSX.utils.json_to_sheet(exportData);
                              const wb = XLSX.utils.book_new();
                              XLSX.utils.book_append_sheet(wb, ws, "Gagnants");
                              
                              // Auto-size columns
                              const colWidths = Object.keys(exportData[0] || {}).map(key => ({
                                wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length))
                              }));
                              ws['!cols'] = colWidths;
                              
                              XLSX.writeFile(wb, `gagnants_${draw.title?.replace(/\s+/g, '_') || 'tirage'}_${new Date().toISOString().split('T')[0]}.xlsx`);
                              toast.success("Export Excel téléchargé !");
                            }}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Export Excel
                          </Button>
                          
                          {/* Email Sending Buttons */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                            disabled={sendingWinnerEmails}
                            onClick={async () => {
                              setSendingWinnerEmails(true);
                              try {
                                const res = await drawsAPI.sendWinnerEmails(draw.id);
                                toast.success(res.data.message);
                                loadEmailStatus(); // Refresh status
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
                              } finally {
                                setSendingWinnerEmails(false);
                              }
                            }}
                          >
                            {sendingWinnerEmails ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-2" />
                            )}
                            Informer les gagnants
                            {emailStatus?.winners_notified > 0 && (
                              <Badge className="ml-2 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                {emailStatus.winners_notified}/{emailStatus.winners_count}
                              </Badge>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                            disabled={sendingReminderEmails}
                            onClick={async () => {
                              setSendingReminderEmails(true);
                              try {
                                const res = await drawsAPI.sendReminderEmails(draw.id);
                                toast.success(res.data.message);
                                loadEmailStatus(); // Refresh status
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
                              } finally {
                                setSendingReminderEmails(false);
                              }
                            }}
                          >
                            {sendingReminderEmails ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Bell className="w-4 h-4 mr-2" />
                            )}
                            Relancer les retardataires
                            {emailStatus?.prizes_pending > 0 && (
                              <Badge className="ml-2 bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                                {emailStatus.prizes_pending}
                              </Badge>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                            disabled={sendingResultsEmails || emailStatus?.results_emails_sent}
                            onClick={async () => {
                              setSendingResultsEmails(true);
                              try {
                                const res = await drawsAPI.sendResultsEmails(draw.id);
                                toast.success(res.data.message);
                                loadEmailStatus(); // Refresh status
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
                              } finally {
                                setSendingResultsEmails(false);
                              }
                            }}
                          >
                            {sendingResultsEmails ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : emailStatus?.results_emails_sent ? (
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            {emailStatus?.results_emails_sent ? "Lien envoyé" : "Envoyer lien résultats"}
                          </Button>
                          
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => navigate(`/draw/${id}/certificate`)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Certificat de Contrôle
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/draw/${id}/results`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Page publique
                      </Button>
                    </div>
                  </div>

                  {/* Unclaimed prizes alert */}
                  {draw?.results?.filter(r => !r.is_withdrawn && !r.is_cancelled).length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">
                            {draw?.results?.filter(r => !r.is_withdrawn && !r.is_cancelled).length} lot(s) en attente de retrait
                          </p>
                          <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                            Envoyez des relances par email aux gagnants qui n'ont pas encore récupéré leur lot.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email Status Card */}
                  {emailStatus && (
                    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium flex items-center gap-2 text-blue-800 dark:text-blue-200">
                            <Mail className="w-4 h-4" />
                            Statut des communications
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={loadEmailStatus}
                            disabled={loadingEmailStatus}
                            className="text-blue-600"
                          >
                            {loadingEmailStatus ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-blue-600">{emailStatus.participants_with_email}</p>
                            <p className="text-xs text-muted-foreground">Avec email</p>
                          </div>
                          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-600">{emailStatus.winners_notified}/{emailStatus.winners_count}</p>
                            <p className="text-xs text-muted-foreground">Gagnants informés</p>
                          </div>
                          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-amber-600">{emailStatus.prizes_pending}</p>
                            <p className="text-xs text-muted-foreground">En attente retrait</p>
                          </div>
                          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-emerald-600">{emailStatus.prizes_collected}</p>
                            <p className="text-xs text-muted-foreground">Lots retirés</p>
                          </div>
                        </div>
                        {emailStatus.results_emails_sent && emailStatus.results_emails_sent_at && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-3 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Lien résultats envoyé le {new Date(emailStatus.results_emails_sent_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Results list */}
                  <div className="space-y-3">
                    {draw?.results?.map((result, index) => {
                      const isWithdrawn = result.is_withdrawn;
                      const isCancelled = result.is_cancelled;
                      const withdrawalKey = `${result.participant?.id}_${result.prize_id}`;
                      const isUpdating = updatingWithdrawal === withdrawalKey;
                      const isSending = sendingReminder === withdrawalKey;
                      const isCancelling = cancellingPrize === withdrawalKey;
                      
                      // Get full prize details from draw.prizes
                      const prizeDetails = draw?.prizes?.find(p => p.id === result.prize_id) || result.prize || {};
                      
                      return (
                        <div 
                          key={index}
                          className={`rounded-xl border transition-all overflow-hidden ${
                            isCancelled
                              ? "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-700 opacity-60"
                              : isWithdrawn 
                                ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" 
                                : "bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 border-amber-200 dark:border-amber-700"
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row">
                            {/* Prize Image/Video */}
                            <div className="w-full sm:w-24 h-32 sm:h-auto flex-shrink-0 bg-muted relative">
                              {prizeDetails.image_url ? (
                                <img 
                                  src={getImageUrl(prizeDetails.image_url)} 
                                  alt={prizeDetails.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : prizeDetails.video_url ? (
                                <div className="w-full h-full bg-gradient-to-br from-red-500/20 to-red-600/20 flex items-center justify-center">
                                  <Video className="w-8 h-8 text-red-500" />
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Gift className="w-8 h-8 text-muted-foreground" />
                                </div>
                              )}
                              {/* Rank Badge */}
                              <div 
                                className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                                  isCancelled ? "bg-slate-400" : isWithdrawn ? "bg-green-500" : "bg-amber-500"
                                }`}
                              >
                                {isCancelled ? <AlertTriangle className="w-4 h-4" /> : isWithdrawn ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                              </div>
                              {/* Partner Logo */}
                              {prizeDetails.partner_logo_url && (
                                <div className="absolute bottom-2 right-2 bg-white rounded-lg p-1 shadow-sm">
                                  <img 
                                    src={getImageUrl(prizeDetails.partner_logo_url)} 
                                    alt={prizeDetails.partner_name}
                                    className="w-8 h-8 object-contain"
                                  />
                                </div>
                              )}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 p-4">
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                {/* Participant Info */}
                                <div className="flex-1">
                                  <p className={`font-semibold text-lg ${isCancelled || isWithdrawn ? "text-muted-foreground" : ""} ${isCancelled ? "line-through" : ""}`}>
                                    {result.participant?.name}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{result.participant?.email}</p>
                                  {result.participant?.reference && (
                                    <p className="text-xs text-muted-foreground font-mono mt-1">
                                      Réf: {result.participant.reference}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Prize Info */}
                                <div className="text-left sm:text-right space-y-1">
                                  <Badge className={`text-sm ${
                                    isCancelled 
                                      ? "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300" 
                                      : isWithdrawn 
                                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" 
                                        : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
                                  }`}>
                                    <Gift className="w-3 h-3 mr-1" />
                                    {prizeDetails.name || result.prize_name}
                                  </Badge>
                                  
                                  {/* Value Badge */}
                                  {prizeDetails.value && (
                                    <Badge className="ml-1 bg-green-500/10 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                                      {prizeDetails.value}
                                    </Badge>
                                  )}
                                  
                                  {/* Partner Info */}
                                  {prizeDetails.partner_name && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1 justify-start sm:justify-end mt-1">
                                      {!prizeDetails.partner_logo_url && prizeDetails.partner_name && (
                                        <span>Offert par {prizeDetails.partner_name}</span>
                                      )}
                                      {prizeDetails.partner_logo_url && (
                                        <>
                                          <span className="hidden sm:inline">par</span>
                                          <span>{prizeDetails.partner_name}</span>
                                        </>
                                      )}
                                    </p>
                                  )}
                                  
                                  {/* Status Info */}
                                  {isWithdrawn && result.withdrawn_at && (
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                      ✓ Retiré le {formatDate(result.withdrawn_at)}
                                    </p>
                                  )}
                                  {isCancelled && (
                                    <p className="text-xs text-slate-500 mt-1">
                                      {result.cancel_reason || "Annulé"}
                                      {result.awaiting_reassignment && " (à remettre en jeu)"}
                                    </p>
                                  )}
                                  {!isWithdrawn && !isCancelled && result.reminder_count > 0 && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                      {result.reminder_count} relance(s) envoyée(s)
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Action buttons */}
                              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-inherit">
                                {!isCancelled && (
                                  <Button
                                    variant={isWithdrawn ? "outline" : "default"}
                                    size="sm"
                                    className={`rounded-full ${
                                      isWithdrawn 
                                        ? "border-slate-300 text-slate-600 hover:bg-muted dark:border-slate-600 dark:text-slate-400" 
                                        : "bg-green-600 hover:bg-green-700"
                                    }`}
                                    onClick={() => handleToggleWithdrawal(result.participant?.id, result.prize_id, isWithdrawn)}
                                    disabled={isUpdating}
                                    data-testid={`withdrawal-btn-${index}`}
                                  >
                                    {isUpdating ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : isWithdrawn ? (
                                      <>
                                        <RotateCcw className="w-4 h-4 mr-1" />
                                        Annuler retrait
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        Marquer retiré
                                      </>
                                    )}
                                  </Button>
                                )}
                                
                                {/* Send reminder button - only for unclaimed */}
                                {!isWithdrawn && !isCancelled && result.participant?.email && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full"
                                    onClick={() => handleSendReminder(result.participant?.id, result.prize_id, result.participant?.email)}
                                    disabled={isSending}
                                    title="Envoyer un email de relance"
                                  >
                                    {isSending ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Mail className="w-4 h-4 mr-1" />
                                        Relancer
                                      </>
                                    )}
                                  </Button>
                                )}
                                
                                {/* Cancel prize button - only for unclaimed */}
                                {!isWithdrawn && !isCancelled && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                                    onClick={() => handleCancelPrize(result.participant?.id, result.prize_id, "cancel")}
                                    disabled={isCancelling}
                                    title="Annuler ce lot"
                                  >
                                    {isCancelling ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <AlertTriangle className="w-4 h-4 mr-1" />
                                        Annuler
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {(!draw?.results || draw.results.length === 0) && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Trophy className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Aucun résultat disponible</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Share Tab - QR Codes & Links */}
            <TabsContent value="share" className="flex-1 p-4 overflow-auto">
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-1">Liens & QR Codes</h3>
                  <p className="text-sm text-muted-foreground">
                    Partagez ces liens ou QR codes pour permettre aux participants de rejoindre votre tirage
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Public Registration Link */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-primary" />
                        Lien de participation public
                      </CardTitle>
                      <CardDescription>
                        Les participants s'inscrivent eux-mêmes via ce lien
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-center p-4 bg-white rounded-lg border">
                        <QRCodeSVG 
                          value={`${window.location.origin}/draw/${id}/register`}
                          size={160}
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">URL</Label>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={`${window.location.origin}/draw/${id}/register`}
                            className="text-xs bg-muted/50"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/draw/${id}/register`);
                              toast.success("Lien copié !");
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.open(`/draw/${id}/register`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Ouvrir
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const svg = document.getElementById('qr-public');
                            if (svg) {
                              const svgData = new XMLSerializer().serializeToString(svg);
                              const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                              const url = URL.createObjectURL(svgBlob);
                              const link = document.createElement('a');
                              link.download = `qr-${draw?.title?.replace(/\s+/g, '-') || 'tirage'}.svg`;
                              link.href = url;
                              link.click();
                              URL.revokeObjectURL(url);
                              toast.success("QR code SVG téléchargé !");
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          SVG
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const svg = document.getElementById('qr-public');
                            if (svg) {
                              const canvas = document.createElement('canvas');
                              canvas.width = 512;
                              canvas.height = 512;
                              const ctx = canvas.getContext('2d');
                              const svgData = new XMLSerializer().serializeToString(svg);
                              const img = new Image();
                              img.onload = () => {
                                ctx.fillStyle = 'white';
                                ctx.fillRect(0, 0, canvas.width, canvas.height);
                                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                                const pngUrl = canvas.toDataURL('image/png');
                                const link = document.createElement('a');
                                link.download = `qr-${draw?.title?.replace(/\s+/g, '-') || 'tirage'}.png`;
                                link.href = pngUrl;
                                link.click();
                                toast.success("QR code PNG téléchargé !");
                              };
                              img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PNG
                        </Button>
                      </div>
                      
                      {/* Hidden QR for download */}
                      <div className="hidden">
                        <QRCodeSVG 
                          id="qr-public"
                          value={`${window.location.origin}/draw/${id}/register`}
                          size={512}
                          level="H"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Shop Mode Link */}
                  <Card className="border-amber-200 dark:border-amber-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Store className="w-5 h-5 text-amber-600" />
                        Mode Boutique
                        <Badge variant="secondary" className="ml-auto bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                          Point de vente
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Inscrivez vos clients directement en magasin
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-center p-4 bg-white rounded-lg border border-amber-200">
                        <QRCodeSVG 
                          value={`${window.location.origin}/draw/${id}/shop`}
                          size={160}
                          level="M"
                          includeMargin={false}
                          fgColor="#d97706"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">URL Mode Boutique</Label>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={`${window.location.origin}/draw/${id}/shop`}
                            className="text-xs bg-muted/50"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/draw/${id}/shop`);
                              toast.success("Lien copié !");
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                          onClick={() => window.open(`/draw/${id}/shop`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Ouvrir
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                          onClick={() => {
                            const svg = document.getElementById('qr-shop');
                            if (svg) {
                              const svgData = new XMLSerializer().serializeToString(svg);
                              const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                              const url = URL.createObjectURL(svgBlob);
                              const link = document.createElement('a');
                              link.download = `qr-${draw?.title?.replace(/\s+/g, '-') || 'tirage'}-boutique.svg`;
                              link.href = url;
                              link.click();
                              URL.revokeObjectURL(url);
                              toast.success("QR code téléchargé !");
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Télécharger
                        </Button>
                      </div>
                      
                      {/* Hidden QR for download */}
                      <div className="hidden">
                        <QRCodeSVG 
                          id="qr-shop"
                          value={`${window.location.origin}/draw/${id}/shop`}
                          size={512}
                          level="H"
                          fgColor="#d97706"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Results Page Link */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="w-5 h-5 text-green-600" />
                      Page des résultats
                    </CardTitle>
                    <CardDescription>
                      Partagez les résultats du tirage après son exécution
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-white rounded-lg border">
                        <QRCodeSVG 
                          value={`${window.location.origin}/draw/${id}/results`}
                          size={80}
                          level="M"
                          fgColor="#16a34a"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={`${window.location.origin}/draw/${id}/results`}
                            className="text-xs bg-muted/50"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/draw/${id}/results`);
                              toast.success("Lien copié !");
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/draw/${id}/results`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Voir les résultats
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* SHOW Page - Countdown / Results Display */}
                <Card className="border-purple-200 dark:border-purple-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-purple-600" />
                      Page SHOW (Affichage public)
                      <Badge variant="secondary" className="ml-auto bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                        Écran / TV
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Avant le tirage : compte à rebours et lots. Après : résultats avec gagnants
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Standard SHOW page */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Page SHOW standard</Label>
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-white rounded-lg border border-purple-200">
                            <QRCodeSVG 
                              id="qr-show"
                              value={`${window.location.origin}/draw/${id}/show`}
                              size={80}
                              level="M"
                              fgColor="#9333ea"
                            />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="flex gap-2">
                              <Input
                                readOnly
                                value={`${window.location.origin}/draw/${id}/show`}
                                className="text-xs bg-muted/50"
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/draw/${id}/show`);
                                  toast.success("Lien copié !");
                                }}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                              onClick={() => window.open(`/draw/${id}/show`, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ouvrir SHOW
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Projector mode */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          Mode Rétroprojecteur
                          <Badge variant="outline" className="text-xs">3 colonnes</Badge>
                        </Label>
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-white rounded-lg border border-purple-200">
                            <QRCodeSVG 
                              id="qr-projector"
                              value={`${window.location.origin}/draw/${id}/show/projector?ratio=16:9`}
                              size={80}
                              level="M"
                              fgColor="#7c3aed"
                            />
                          </div>
                          <div className="flex-1 space-y-2">
                            <Select
                              defaultValue="16:9"
                              onValueChange={(ratio) => {
                                const url = `${window.location.origin}/draw/${id}/show/projector?ratio=${encodeURIComponent(ratio)}`;
                                window.open(url, '_blank');
                              }}
                            >
                              <SelectTrigger className="text-xs bg-muted/50">
                                <SelectValue placeholder="Choisir le ratio" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="16:9">16:9 (PC/TV)</SelectItem>
                                <SelectItem value="4:3">4:3 (Ancien écran)</SelectItem>
                                <SelectItem value="9:16">9:16 (Mobile/Vertical)</SelectItem>
                                <SelectItem value="1:1">1:1 (Carré)</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                              onClick={() => window.open(`/draw/${id}/show/projector?ratio=16:9`, '_blank')}
                            >
                              <Monitor className="w-4 h-4 mr-1" />
                              Ouvrir Projecteur
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Results SHOW mode - Only shown when draw is completed */}
                      {isCompleted && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            Page Résultats SHOW
                            <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">Confettis</Badge>
                          </Label>
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-white rounded-lg border border-green-200">
                              <QRCodeSVG 
                                id="qr-results-show"
                                value={`${window.location.origin}/draw/${id}/show/results`}
                                size={80}
                                level="M"
                                fgColor="#16a34a"
                              />
                            </div>
                            <div className="flex-1 space-y-2">
                              <Input
                                readOnly
                                value={`${window.location.origin}/draw/${id}/show/results`}
                                className="text-xs bg-muted/50"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full border-green-300 hover:bg-green-50 dark:hover:bg-green-950 text-green-700"
                                onClick={() => window.open(`/draw/${id}/show/results`, '_blank')}
                              >
                                <Trophy className="w-4 h-4 mr-1" />
                                Ouvrir Résultats SHOW
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Download QR codes */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const svg = document.getElementById('qr-show');
                          if (svg) {
                            const canvas = document.createElement('canvas');
                            canvas.width = 512;
                            canvas.height = 512;
                            const ctx = canvas.getContext('2d');
                            const svgData = new XMLSerializer().serializeToString(svg);
                            const img = new Image();
                            img.onload = () => {
                              ctx.fillStyle = 'white';
                              ctx.fillRect(0, 0, canvas.width, canvas.height);
                              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                              const pngUrl = canvas.toDataURL('image/png');
                              const link = document.createElement('a');
                              link.download = `qr-show-${draw?.title?.replace(/\s+/g, '-') || 'tirage'}.png`;
                              link.href = pngUrl;
                              link.click();
                              toast.success("QR SHOW téléchargé !");
                            };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        QR SHOW (PNG)
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const svg = document.getElementById('qr-projector');
                          if (svg) {
                            const canvas = document.createElement('canvas');
                            canvas.width = 512;
                            canvas.height = 512;
                            const ctx = canvas.getContext('2d');
                            const svgData = new XMLSerializer().serializeToString(svg);
                            const img = new Image();
                            img.onload = () => {
                              ctx.fillStyle = 'white';
                              ctx.fillRect(0, 0, canvas.width, canvas.height);
                              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                              const pngUrl = canvas.toDataURL('image/png');
                              const link = document.createElement('a');
                              link.download = `qr-projector-${draw?.title?.replace(/\s+/g, '-') || 'tirage'}.png`;
                              link.href = pngUrl;
                              link.click();
                              toast.success("QR Projecteur téléchargé !");
                            };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        QR Projecteur (PNG)
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Design Tab */}
            <TabsContent value="design" className="flex-1 p-4 overflow-auto">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Background */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Fond de page
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-4 flex-wrap">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="color"
                          {...register("background_type")}
                          className="rounded"
                        />
                        <span className="text-sm">Couleur</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="image"
                          {...register("background_type")}
                          className="rounded"
                        />
                        <span className="text-sm">Image</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="video"
                          {...register("background_type")}
                          className="rounded"
                        />
                        <span className="text-sm flex items-center gap-1">
                          <Video className="w-4 h-4 text-red-500" />
                          Vidéo YouTube
                        </span>
                      </label>
                    </div>

                    {watchedValues.background_type === "color" && (
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          {...register("background_color")}
                          className="w-12 h-12 rounded cursor-pointer"
                        />
                        <Input
                          {...register("background_color")}
                          placeholder="#1e3a5f"
                          className="flex-1"
                        />
                      </div>
                    )}

                    {watchedValues.background_type === "image" && (
                      <div className="space-y-3">
                        {watchedValues.background_image ? (
                          <div className="relative">
                            <img 
                              src={watchedValues.background_image} 
                              alt="Background" 
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={() => {
                                setValue("background_image", "");
                                setValue("background_type", "color");
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed rounded-lg p-6 text-center">
                            <Image className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                            <p className="text-sm text-muted-foreground mb-3">
                              Uploadez une image de fond
                            </p>
                            <input
                              type="file"
                              ref={bgInputRef}
                              className="hidden"
                              accept="image/*"
                              onChange={handleUploadBackground}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => bgInputRef.current?.click()}
                              disabled={uploadingBg}
                            >
                              {uploadingBg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                              Choisir une image
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {watchedValues.background_type === "video" && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Video className="w-5 h-5 text-red-500" />
                          <Input
                            {...register("background_video_url")}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="flex-1"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          La vidéo sera lue en boucle avec un overlay sombre à 70% pour la lisibilité
                        </p>
                        {watchedValues.background_video_url && (
                          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                            <div className="absolute inset-0 bg-black/70 z-10 flex items-center justify-center">
                              <span className="text-white/50 text-xs">Aperçu avec overlay 70%</span>
                            </div>
                            <iframe
                              src={`https://www.youtube.com/embed/${watchedValues.background_video_url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/)?.[1]}?autoplay=0&mute=1`}
                              className="w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Accent Color */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Couleur d'accent</CardTitle>
                    <CardDescription className="text-xs">
                      Utilisée pour les boutons et les numéros de lot
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        {...register("accent_color")}
                        className="w-12 h-12 rounded cursor-pointer"
                      />
                      <Input
                        {...register("accent_color")}
                        placeholder="#eab308"
                        className="flex-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Partner Logos Carousel */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Carrousel de partenaires</span>
                      <Switch
                        checked={watchedValues.partner_carousel_enabled || false}
                        onCheckedChange={(checked) => setValue("partner_carousel_enabled", checked)}
                      />
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Affichez les logos de vos partenaires sur la page des résultats
                    </CardDescription>
                  </CardHeader>
                  {watchedValues.partner_carousel_enabled && (
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm">Titre du bandeau</Label>
                        <Input
                          {...register("partner_carousel_title")}
                          placeholder="Merci à nos partenaires"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm">Logos des partenaires</Label>
                        <div className="flex flex-wrap gap-3">
                          {(watchedValues.partner_logos || []).map((logo, index) => (
                            <div key={index} className="relative group">
                              <img 
                                src={getImageUrl(logo.url)} 
                                alt={logo.name || `Partenaire ${index + 1}`}
                                className="h-14 w-14 object-contain rounded-lg bg-white p-1 border"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newLogos = [...(watchedValues.partner_logos || [])];
                                  newLogos.splice(index, 1);
                                  setValue("partner_logos", newLogos);
                                }}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {/* Upload new logo */}
                          <input
                            type="file"
                            id="editor-partner-logo-upload"
                            className="hidden"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const formData = new FormData();
                                formData.append("file", file);
                                const res = await api.post("/upload", formData, {
                                  headers: { "Content-Type": "multipart/form-data" }
                                });
                                const newLogos = [...(watchedValues.partner_logos || []), { url: res.data.url, name: file.name.split('.')[0] }];
                                setValue("partner_logos", newLogos);
                                toast.success("Logo partenaire ajouté");
                              } catch (error) {
                                toast.error("Erreur lors de l'upload");
                              }
                              e.target.value = '';
                            }}
                          />
                          <label 
                            htmlFor="editor-partner-logo-upload"
                            className="h-14 w-14 flex items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                            title="Upload un fichier"
                          >
                            <Plus className="w-5 h-5 text-muted-foreground" />
                          </label>
                          {/* Select from gallery */}
                          <button
                            type="button"
                            onClick={() => setShowMediaSelector(true)}
                            className="h-14 px-3 flex items-center gap-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors text-muted-foreground hover:text-foreground"
                            title="Sélectionner depuis la galerie"
                          >
                            <FolderOpen className="w-4 h-4" />
                            <span className="text-xs">Galerie</span>
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Photos Tab - Photo Wall Manager */}
            <TabsContent value="photos" className="flex-1 overflow-auto">
              <PhotoWallManager drawId={id} drawTitle={draw?.title} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right Panel - Preview (hidden on mobile, shown at bottom on tablet) */}
      <div className="hidden lg:flex w-full lg:w-1/2 flex-col bg-muted">
        {/* Preview Header */}
        <div className="p-4 bg-card border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Aperçu</span>
            <Badge variant="outline" className="text-xs ml-2">
              16:9
            </Badge>
          </div>
          <div className="flex gap-1">
            <Button
              variant={previewMode === "show" ? "default" : "outline"}
              size="sm"
              onClick={() => setPreviewMode("show")}
              className="text-xs"
            >
              <Monitor className="w-3 h-3 mr-1" />
              Page SHOW
            </Button>
            <Button
              variant={previewMode === "results" ? "default" : "outline"}
              size="sm"
              onClick={() => setPreviewMode("results")}
              className="text-xs"
            >
              <Trophy className="w-3 h-3 mr-1" />
              Résultats
            </Button>
            <Button
              variant={previewMode === "register" ? "default" : "outline"}
              size="sm"
              onClick={() => setPreviewMode("register")}
              className="text-xs"
            >
              <UserPlus className="w-3 h-3 mr-1" />
              Inscription
            </Button>
          </div>
        </div>

        {/* Preview Content - 16:9 Aspect Ratio Container */}
        <div className="flex-1 p-6 flex items-center justify-center overflow-auto">
          <div className="w-full max-w-3xl">
            {/* TV/Monitor Frame */}
            <div className="relative">
              {/* Screen bezel effect */}
              <div className="bg-gradient-to-b from-slate-700 to-slate-900 rounded-xl p-2 shadow-2xl">
                {/* Inner frame */}
                <div className="bg-black rounded-lg overflow-hidden">
                  {/* 16:9 Aspect Ratio Container */}
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <div className="absolute inset-0">
                      <DrawPreview draw={previewDraw} previewMode={previewMode} />
                    </div>
                  </div>
                </div>
              </div>
              {/* Monitor stand */}
              <div className="flex justify-center mt-1">
                <div className="w-20 h-2 bg-gradient-to-b from-slate-600 to-slate-800 rounded-b-lg" />
              </div>
              <div className="flex justify-center">
                <div className="w-32 h-1 bg-gradient-to-b from-slate-700 to-slate-900 rounded-b-lg" />
              </div>
            </div>
            {/* Resolution indicator */}
            <p className="text-center text-xs text-muted-foreground mt-3">
              Simulation écran 16:9 (1920×1080)
            </p>
          </div>
        </div>
      </div>

      {/* CSV Mapping Dialog */}
      <Dialog open={showCsvMapping} onOpenChange={setShowCsvMapping}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Mapper les colonnes
            </DialogTitle>
            <DialogDescription>
              Associez les colonnes de votre fichier <strong>{csvFileName}</strong> aux champs TIREO
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Preview of first rows */}
            <div className="bg-muted/50 rounded-lg p-3 overflow-x-auto">
              <p className="text-xs text-muted-foreground mb-2">
                Aperçu des données ({csvData.length} lignes)
              </p>
              <table className="text-xs w-full">
                <thead>
                  <tr>
                    {csvColumns.map((col, idx) => (
                      <th key={idx} className="px-2 py-1 text-left bg-muted font-medium">
                        {col || `Col ${idx + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.slice(0, 3).map((row, rowIdx) => (
                    <tr key={rowIdx} className="border-t border-border">
                      {csvColumns.map((_, colIdx) => (
                        <td key={colIdx} className="px-2 py-1 truncate max-w-[150px]">
                          {String(row[colIdx] || "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvData.length > 3 && (
                <p className="text-xs text-muted-foreground mt-2">
                  ... et {csvData.length - 3} autres lignes
                </p>
              )}
            </div>

            {/* Mapping Fields */}
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-1">
                    Prénom
                  </Label>
                  <Select
                    value={columnMapping.prenom || "none"}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, prenom: val === "none" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une colonne" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ne pas importer --</SelectItem>
                      {csvColumns.map((col, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {col || `Colonne ${idx + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-1">
                    Nom <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={columnMapping.nom || "none"}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, nom: val === "none" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une colonne" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ne pas importer --</SelectItem>
                      {csvColumns.map((col, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {col || `Colonne ${idx + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Email</Label>
                  <Select
                    value={columnMapping.email || "none"}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, email: val === "none" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une colonne" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ne pas importer --</SelectItem>
                      {csvColumns.map((col, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {col || `Colonne ${idx + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Téléphone</Label>
                  <Select
                    value={columnMapping.phone || "none"}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, phone: val === "none" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une colonne" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ne pas importer --</SelectItem>
                      {csvColumns.map((col, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {col || `Colonne ${idx + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">N° de ticket / Tombola</Label>
                  <Select
                    value={columnMapping.ticket_number || "none"}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, ticket_number: val === "none" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une colonne" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ne pas importer --</SelectItem>
                      {csvColumns.map((col, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {col || `Colonne ${idx + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">
                    {draw?.custom_field_label || "Champ personnalisé"}
                  </Label>
                  <Select
                    value={columnMapping.custom_field || "none"}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, custom_field: val === "none" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une colonne" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Ne pas importer --</SelectItem>
                      {csvColumns.map((col, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {col || `Colonne ${idx + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-4 h-4 text-green-600" />
                <span className="font-medium text-sm text-green-700 dark:text-green-400">
                  Prêt à importer {csvData.length} participant(s)
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Les colonnes non mappées seront ignorées. Le prénom et le nom seront combinés.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCsvMapping(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleImportCsv} 
              disabled={importingCsv || (!columnMapping.nom && !columnMapping.prenom)}
              className="bg-green-600 hover:bg-green-700"
            >
              {importingCsv ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Importer {csvData.length} participants
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate PDF Modal */}
      <Dialog open={showCertificateModal} onOpenChange={setShowCertificateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Certificat de Contrôle du Tirage
            </DialogTitle>
            <DialogDescription>
              Générez un document PDF officiel certifiant le bon déroulement du tirage au sort
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Draw Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">Résumé du tirage</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Titre :</span> {draw?.title}</div>
                <div><span className="text-muted-foreground">Organisateur :</span> {draw?.organizer_name}</div>
                <div><span className="text-muted-foreground">Date du tirage :</span> {draw?.draw_date ? new Date(draw.draw_date).toLocaleDateString('fr-FR') : "N/A"}</div>
                <div><span className="text-muted-foreground">Participants :</span> {draw?.participants?.length || 0}</div>
                <div><span className="text-muted-foreground">Gagnants :</span> {draw?.results?.length || 0}</div>
                <div><span className="text-muted-foreground">Lots distribués :</span> {draw?.results?.filter(r => r.is_withdrawn).length || 0}</div>
              </div>
            </div>

            {/* Winners Preview */}
            {draw?.results?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Liste des gagnants</h4>
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left p-2">Rang</th>
                        <th className="text-left p-2">Gagnant</th>
                        <th className="text-left p-2">Lot</th>
                        <th className="text-left p-2">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draw.results.map((result, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{idx + 1}</td>
                          <td className="p-2">{result.participant?.name || "N/A"}</td>
                          <td className="p-2">{result.prize?.name || result.prize_name || "N/A"}</td>
                          <td className="p-2">
                            <Badge variant={result.is_withdrawn ? "default" : result.is_cancelled ? "destructive" : "secondary"} className="text-xs">
                              {result.is_withdrawn ? "Retiré" : result.is_cancelled ? "Annulé" : "En attente"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Supervisor Information */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Pen className="w-4 h-4" />
                Informations du contrôleur
              </h4>
              
              {/* Type de contrôleur */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCertificateData({ ...certificateData, supervisor_type: "huissier" })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    certificateData.supervisor_type === "huissier" 
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" 
                      : "border-border hover:border-blue-300"
                  }`}
                >
                  <p className="font-medium text-sm">Huissier de justice</p>
                  <p className="text-xs text-muted-foreground">Contrôle officiel certifié</p>
                </button>
                <button
                  type="button"
                  onClick={() => setCertificateData({ ...certificateData, supervisor_type: "controleur" })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    certificateData.supervisor_type === "controleur" 
                      ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                      : "border-border hover:border-green-300"
                  }`}
                >
                  <p className="font-medium text-sm">Contrôleur (bénévole)</p>
                  <p className="text-xs text-muted-foreground">Témoin du bon déroulement</p>
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom complet *</Label>
                  <Input
                    placeholder="Jean Dupont"
                    value={certificateData.supervisor_name}
                    onChange={(e) => setCertificateData({ ...certificateData, supervisor_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{certificateData.supervisor_type === "huissier" ? "N° d'agrément / Étude" : "Fonction / Rôle"}</Label>
                  <Input
                    placeholder={certificateData.supervisor_type === "huissier" ? "Étude de Me Dupont, Paris" : "Responsable, Président..."}
                    value={certificateData.supervisor_role}
                    onChange={(e) => setCertificateData({ ...certificateData, supervisor_role: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de contrôle</Label>
                  <Input
                    type="date"
                    value={certificateData.supervisor_date}
                    onChange={(e) => setCertificateData({ ...certificateData, supervisor_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure</Label>
                  <Input
                    type="time"
                    value={certificateData.supervisor_time}
                    onChange={(e) => setCertificateData({ ...certificateData, supervisor_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observations (optionnel)</Label>
                <Textarea
                  placeholder="Notes ou observations sur le déroulement du tirage..."
                  value={certificateData.notes}
                  onChange={(e) => setCertificateData({ ...certificateData, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            {/* Signature Canvas - IN APP */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Pen className="w-4 h-4" />
                  Signature manuscrite
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      const ctx = canvas.getContext('2d');
                      ctx.fillStyle = 'white';
                      ctx.fillRect(0, 0, canvas.width, canvas.height);
                      setCertificateData({ ...certificateData, signature: "" });
                    }
                  }}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Effacer
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Signez avec votre doigt (écran tactile) ou votre souris
              </p>
              <div className="border-2 border-dashed rounded-lg bg-white overflow-hidden">
                <canvas
                  ref={signatureCanvasRef}
                  width={500}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  style={{ touchAction: 'none' }}
                  onMouseDown={(e) => {
                    setIsDrawing(true);
                    const canvas = signatureCanvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    const ctx = canvas.getContext('2d');
                    ctx.beginPath();
                    ctx.moveTo(
                      (e.clientX - rect.left) * (canvas.width / rect.width),
                      (e.clientY - rect.top) * (canvas.height / rect.height)
                    );
                  }}
                  onMouseMove={(e) => {
                    if (!isDrawing) return;
                    const canvas = signatureCanvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    const ctx = canvas.getContext('2d');
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.strokeStyle = '#1e3a8a';
                    ctx.lineTo(
                      (e.clientX - rect.left) * (canvas.width / rect.width),
                      (e.clientY - rect.top) * (canvas.height / rect.height)
                    );
                    ctx.stroke();
                  }}
                  onMouseUp={() => {
                    setIsDrawing(false);
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      setCertificateData({ ...certificateData, signature: canvas.toDataURL('image/png') });
                    }
                  }}
                  onMouseLeave={() => {
                    if (isDrawing) {
                      setIsDrawing(false);
                      const canvas = signatureCanvasRef.current;
                      if (canvas) {
                        setCertificateData({ ...certificateData, signature: canvas.toDataURL('image/png') });
                      }
                    }
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setIsDrawing(true);
                    const canvas = signatureCanvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    const touch = e.touches[0];
                    const ctx = canvas.getContext('2d');
                    ctx.beginPath();
                    ctx.moveTo(
                      (touch.clientX - rect.left) * (canvas.width / rect.width),
                      (touch.clientY - rect.top) * (canvas.height / rect.height)
                    );
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    if (!isDrawing) return;
                    const canvas = signatureCanvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    const touch = e.touches[0];
                    const ctx = canvas.getContext('2d');
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.strokeStyle = '#1e3a8a';
                    ctx.lineTo(
                      (touch.clientX - rect.left) * (canvas.width / rect.width),
                      (touch.clientY - rect.top) * (canvas.height / rect.height)
                    );
                    ctx.stroke();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setIsDrawing(false);
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      setCertificateData({ ...certificateData, signature: canvas.toDataURL('image/png') });
                    }
                  }}
                />
              </div>
              {certificateData.signature && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Signature enregistrée
                </p>
              )}
            </div>

            {/* Horodatage */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">Horodatage automatique</Badge>
              <span>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCertificateModal(false)}>
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={!certificateData.supervisor_name || !certificateData.signature}
              onClick={() => {
                // Generate and download PDF certificate with signature
                const printWindow = window.open('', '_blank');
                const htmlContent = `
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Certificat de Contrôle - ${draw?.title}</title>
                    <style>
                      @page { margin: 20mm; }
                      body { 
                        font-family: 'Segoe UI', Arial, sans-serif; 
                        line-height: 1.6;
                        color: #1a1a1a;
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 20px;
                      }
                      .header { 
                        text-align: center; 
                        border-bottom: 3px solid #3b82f6; 
                        padding-bottom: 20px;
                        margin-bottom: 30px;
                      }
                      .header h1 { 
                        color: #1e40af; 
                        font-size: 24px;
                        margin: 0;
                      }
                      .header p { 
                        color: #6b7280; 
                        margin: 5px 0 0;
                      }
                      .section { 
                        margin-bottom: 25px;
                        padding: 15px;
                        background: #f9fafb;
                        border-radius: 8px;
                        border-left: 4px solid #3b82f6;
                      }
                      .section h2 { 
                        font-size: 14px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        color: #4b5563;
                        margin: 0 0 15px;
                      }
                      .grid { 
                        display: grid; 
                        grid-template-columns: repeat(2, 1fr); 
                        gap: 10px;
                      }
                      .grid-item { font-size: 13px; }
                      .grid-item strong { color: #374151; }
                      table { 
                        width: 100%; 
                        border-collapse: collapse; 
                        font-size: 12px;
                        margin-top: 10px;
                      }
                      th, td { 
                        border: 1px solid #e5e7eb; 
                        padding: 8px 12px; 
                        text-align: left;
                      }
                      th { 
                        background: #f3f4f6;
                        font-weight: 600;
                      }
                      .signature-box {
                        margin-top: 40px;
                        padding: 20px;
                        border: 2px solid #1e40af;
                        border-radius: 8px;
                      }
                      .signature-box h3 {
                        color: #1e40af;
                        margin: 0 0 15px;
                        font-size: 14px;
                      }
                      .signature-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 40px;
                      }
                      .signature-area {
                        border-bottom: 1px solid #9ca3af;
                        height: 80px;
                        margin-top: 10px;
                      }
                      .attestation {
                        background: #fef3c7;
                        border: 1px solid #f59e0b;
                        padding: 15px;
                        border-radius: 8px;
                        margin-top: 20px;
                        font-size: 12px;
                      }
                      .attestation strong { color: #92400e; }
                      .footer {
                        margin-top: 30px;
                        padding-top: 20px;
                        border-top: 1px solid #e5e7eb;
                        font-size: 11px;
                        color: #6b7280;
                        text-align: center;
                      }
                      .horodatage {
                        background: #e0e7ff;
                        padding: 8px 15px;
                        border-radius: 4px;
                        font-size: 11px;
                        display: inline-block;
                        margin-top: 10px;
                      }
                    </style>
                  </head>
                  <body>
                    <div class="header">
                      <h1>CERTIFICAT DE CONTRÔLE DU TIRAGE AU SORT</h1>
                      <p>Document officiel attestant le bon déroulement de l'opération</p>
                    </div>

                    <div class="section">
                      <h2>Informations du tirage</h2>
                      <div class="grid">
                        <div class="grid-item"><strong>Titre :</strong> ${draw?.title || 'N/A'}</div>
                        <div class="grid-item"><strong>Organisateur :</strong> ${draw?.organizer_name || 'N/A'}</div>
                        <div class="grid-item"><strong>Date du tirage :</strong> ${draw?.draw_date ? new Date(draw.draw_date).toLocaleDateString('fr-FR') : 'N/A'}</div>
                        <div class="grid-item"><strong>Mode de tirage :</strong> ${draw?.draw_mode === 'order' ? 'Par ordre d\'inscription' : 'Aléatoire'}</div>
                        <div class="grid-item"><strong>Nombre de participants :</strong> ${draw?.participants?.length || 0}</div>
                        <div class="grid-item"><strong>Nombre de lots :</strong> ${draw?.prizes?.length || 0}</div>
                      </div>
                    </div>

                    <div class="section">
                      <h2>Liste des gagnants</h2>
                      <table>
                        <thead>
                          <tr>
                            <th>Rang</th>
                            <th>Nom du gagnant</th>
                            <th>Lot attribué</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${(draw?.results || []).map((r, i) => `
                            <tr>
                              <td>${i + 1}</td>
                              <td>${r.participant?.name || 'N/A'}</td>
                              <td>${r.prize?.name || r.prize_name || 'N/A'}</td>
                              <td>${r.is_withdrawn ? 'Retiré' : r.is_cancelled ? 'Annulé' : 'En attente'}</td>
                            </tr>
                          `).join('')}
                        </tbody>
                      </table>
                    </div>

                    <div class="signature-box">
                      <h3>CERTIFICATION DU CONTRÔLE ${certificateData.supervisor_type === 'huissier' ? '(HUISSIER DE JUSTICE)' : '(CONTRÔLEUR BÉNÉVOLE)'}</h3>
                      
                      <div class="attestation">
                        <strong>Je soussigné(e), ${certificateData.supervisor_name}${certificateData.supervisor_role ? `, ${certificateData.supervisor_role}` : ''}, en qualité de ${certificateData.supervisor_type === 'huissier' ? 'Huissier de Justice' : 'Contrôleur bénévole'},</strong>
                        certifie avoir contrôlé le déroulement du tirage au sort mentionné ci-dessus et atteste que celui-ci s'est déroulé 
                        de manière équitable, transparente et conforme aux règles établies, sans aucune irrégularité constatée.
                      </div>

                      ${certificateData.notes ? `
                        <div style="margin-top: 15px;">
                          <strong style="font-size: 12px;">Observations :</strong>
                          <p style="font-size: 12px; margin: 5px 0 0;">${certificateData.notes}</p>
                        </div>
                      ` : ''}

                      <div class="signature-grid">
                        <div>
                          <p style="margin: 20px 0 5px; font-size: 12px;"><strong>${certificateData.supervisor_type === 'huissier' ? 'Huissier de Justice' : 'Contrôleur'} :</strong></p>
                          <p style="margin: 0; font-size: 14px;">${certificateData.supervisor_name}</p>
                          ${certificateData.supervisor_role ? `<p style="margin: 0; font-size: 12px; color: #6b7280;">${certificateData.supervisor_role}</p>` : ''}
                        </div>
                        <div>
                          <p style="margin: 20px 0 5px; font-size: 12px;"><strong>Signature :</strong></p>
                          ${certificateData.signature ? `
                            <img src="${certificateData.signature}" alt="Signature" style="max-width: 200px; height: auto; border: 1px solid #e5e7eb; border-radius: 4px;" />
                          ` : '<div class="signature-area"></div>'}
                        </div>
                      </div>

                      <div style="margin-top: 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; font-size: 12px;">
                        <div><strong>Date :</strong> ${certificateData.supervisor_date}</div>
                        <div><strong>Heure :</strong> ${certificateData.supervisor_time}</div>
                      </div>
                    </div>

                    <div class="footer">
                      <div class="horodatage">
                        Document généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
                      </div>
                      <p style="margin-top: 15px;">
                        Ce document est généré par la plateforme TIREO et fait foi du bon déroulement du tirage au sort.<br>
                        Référence : ${draw?.id || 'N/A'}
                      </p>
                    </div>
                  </body>
                  </html>
                `;
                
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                
                // Wait for content to load then trigger print
                printWindow.onload = () => {
                  printWindow.print();
                };
                
                // Save certificate data to draw
                api.put(`/draws/${id}`, {
                  certificate_data: {
                    ...certificateData,
                    generated_at: new Date().toISOString()
                  }
                }).then(() => {
                  toast.success("Certificat généré et archivé !");
                }).catch(() => {
                  toast.success("Certificat généré !");
                });
                
                setShowCertificateModal(false);
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Prize Modal */}
      <Dialog open={!!editingPrize} onOpenChange={(open) => !open && setEditingPrize(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Modifier le lot
            </DialogTitle>
            <DialogDescription>
              Modifiez les informations du lot ci-dessous
            </DialogDescription>
          </DialogHeader>

          {editingPrize && (
            <div className="space-y-4 py-4">
              {/* Name and Quantity */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label>Nom du lot *</Label>
                  <Input
                    placeholder="Nom du lot"
                    value={editingPrize.name || ""}
                    onChange={(e) => setEditingPrize({...editingPrize, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantité</Label>
                  <Input
                    type="number"
                    min={1}
                    value={editingPrize.quantity || 1}
                    onChange={(e) => setEditingPrize({...editingPrize, quantity: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Description du lot"
                  value={editingPrize.description || ""}
                  onChange={(e) => setEditingPrize({...editingPrize, description: e.target.value})}
                  rows={2}
                />
              </div>

              {/* Value */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  Valeur estimée
                </Label>
                <Input
                  placeholder="Ex: 500€"
                  value={editingPrize.value || ""}
                  onChange={(e) => setEditingPrize({...editingPrize, value: e.target.value})}
                />
              </div>

              {/* Media Type */}
              <div className="space-y-2">
                <Label>Média</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={editingPrize.media_type === "image" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditingPrize({...editingPrize, media_type: "image", video_url: ""})}
                  >
                    <Image className="w-4 h-4 mr-1" />
                    Image
                  </Button>
                  <Button
                    type="button"
                    variant={editingPrize.media_type === "video" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditingPrize({...editingPrize, media_type: "video", image_url: ""})}
                  >
                    <Video className="w-4 h-4 mr-1" />
                    Vidéo YouTube
                  </Button>
                </div>
              </div>

              {/* Image Upload */}
              {editingPrize.media_type === "image" && (
                <div className="space-y-2">
                  <Label>Image du lot</Label>
                  <div className="flex items-center gap-3">
                    {editingPrize.image_url ? (
                      <img 
                        src={getImageUrl(editingPrize.image_url)} 
                        alt="Lot" 
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center">
                        <Image className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <input
                        type="file"
                        ref={editPrizeImageRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleUploadEditPrizeImage}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => editPrizeImageRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {editingPrize.image_url ? "Changer" : "Uploader"}
                      </Button>
                      {editingPrize.image_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPrize({...editingPrize, image_url: ""})}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Video URL */}
              {editingPrize.media_type === "video" && (
                <div className="space-y-2">
                  <Label>URL YouTube</Label>
                  <Input
                    placeholder="https://youtube.com/watch?v=..."
                    value={editingPrize.video_url || ""}
                    onChange={(e) => setEditingPrize({...editingPrize, video_url: e.target.value})}
                  />
                </div>
              )}

              {/* Partner Section */}
              <div className="border-t pt-4 space-y-3">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  Partenaire (optionnel)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Nom du partenaire</Label>
                    <Input
                      placeholder="Nom du partenaire"
                      value={editingPrize.partner_name || ""}
                      onChange={(e) => setEditingPrize({...editingPrize, partner_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Logo du partenaire</Label>
                    <div className="flex items-center gap-2">
                      {editingPrize.partner_logo_url ? (
                        <img 
                          src={getImageUrl(editingPrize.partner_logo_url)} 
                          alt="" 
                          className="w-10 h-10 object-contain rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                          <Image className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <input
                        type="file"
                        ref={editPrizePartnerLogoRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleUploadEditPrizePartnerLogo}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => editPrizePartnerLogoRef.current?.click()}
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                      {editingPrize.partner_logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPrize({...editingPrize, partner_logo_url: ""})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pickup Instructions */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-purple-500" />
                  Instructions de retrait
                </Label>
                <Input
                  placeholder="Ex: Retrait en magasin sur présentation du code"
                  value={editingPrize.pickup_instructions || ""}
                  onChange={(e) => setEditingPrize({...editingPrize, pickup_instructions: e.target.value})}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingPrize(null)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdatePrize}
              disabled={savingPrize || !editingPrize?.name?.trim()}
            >
              {savingPrize ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Selector for partner logos */}
      <MediaSelector
        open={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelect={(selectedMedia) => {
          // selectedMedia is an array when multiple=true
          const newLogos = selectedMedia.map(m => ({ url: m.url, name: m.name }));
          const existingLogos = watchedValues.partner_logos || [];
          setValue("partner_logos", [...existingLogos, ...newLogos]);
          toast.success(`${newLogos.length} logo(s) ajouté(s)`);
        }}
        multiple={true}
        categoryFilter="Logos partenaires"
        mediaTypeFilter="image"
        title="Sélectionner des logos partenaires"
      />
    </div>
  );
}
